import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getSupabaseClient,
  hasSupabaseConfig,
  updateEmailSettings,
  getManageAuditLogs,
  getManageDatabase,
  getManageDeploymentPlan,
  getManageBootstrap,
  getManageBillingSummary,
  getManageConfig,
  getManageEmailDeliveries,
  getManageFreePlanUsage,
  getManageLedger,
  getManagePayments,
  getManageProviderUsage,
  getManageRunDetail,
  getManageRuns,
  getManageServices,
  getManageSourceUsage,
  getManageSummary,
  getManageUser,
  getManageUsers,
  getRunComputeJobs,
  getRunComputePerformance,
  loginManage,
  notifyAuthChange,
  manageActionRun,
  refundManagePayment,
  resolveApiUrl,
  sendManageTestEmail,
  testManageTarget,
  deleteManageProvider,
  updateManageUser,
  updateManageConfig,
  updateManageProvider,
  updateManageSource,
  adjustManageUserCredits,
} from '../services/api';

type TabKey = 'overview' | 'runs' | 'users' | 'billing' | 'config' | 'services' | 'database';

type ManagePanelProps = {
  initialKey?: string;
};

type ManageService = Record<string, any>;
type ManageRun = Record<string, any>;
type ManageUser = Record<string, any>;
type ManagePayment = Record<string, any>;
type ManageLedgerRow = Record<string, any>;
type ManageEmailDelivery = Record<string, any>;
type ManageStageBucket = {
  label?: string;
  running?: number;
  queued?: number;
  runs?: Array<Record<string, any>>;
};

type ManageSummary = {
  updatedAt?: string;
  overview?: Record<string, any>;
  usage?: Record<string, any>;
  stages?: Record<string, ManageStageBucket>;
  services?: ManageService[];
  pressure?: Record<string, any>;
  config?: Record<string, any>;
  recommendations?: string[];
};

type ManageDatabaseAudit = {
  updatedAt?: string;
  database?: Record<string, any>;
  cache?: Record<string, any>;
  usage?: Record<string, any>;
  services?: ManageService[];
  auditLogs?: Array<Record<string, any>>;
  capacity?: Record<string, any>;
  freePlanUsage?: ManageFreePlanUsage | null;
  notes?: string[];
  pressure?: Record<string, any>;
  recommendations?: string[];
};

type ManageFreePlanUsage = {
  updatedAt?: string;
  plan?: string;
  basis?: Record<string, any>;
  dimensions?: Array<Record<string, any>>;
  notes?: string[];
};

type ManageBillingOps = {
  updatedAt?: string;
  summary?: Record<string, any>;
  payments?: ManagePayment[];
  paymentsTotal?: number;
  ledger?: ManageLedgerRow[];
  ledgerTotal?: number;
  deliveries?: ManageEmailDelivery[];
  deliveriesTotal?: number;
};

type DeploymentPlan = {
  updatedAt?: string;
  counts?: Record<string, number>;
  minimumPageFirst?: Record<string, any>;
  steps?: Array<Record<string, any>>;
};

type ProviderUsageWindow = {
  calls?: number;
  okCalls?: number;
  errorCalls?: number;
  skippedCalls?: number;
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
  totalLatencyMs?: number;
  avgLatencyMs?: number | null;
};
type ProviderQuotaState = {
  limit?: number | null;
  used?: number;
  remaining?: number | null;
  exhausted?: boolean;
};

type ProviderUsageRow = {
  id: string;
  taskType?: ProviderKind | string;
  provider?: string;
  model?: string | null;
  baseUrl?: string | null;
  enabled?: boolean;
  rps?: number;
  concurrency?: number;
  timeoutSeconds?: number;
  vectorSpace?: string | null;
  dimensions?: number | null;
  apiKeyRef?: string | null;
  hasApiKey?: boolean;
  tokenBudgets?: {
    last5h?: number | null;
    last24h?: number | null;
    last7d?: number | null;
    total?: number | null;
  };
  quota?: {
    last5h?: ProviderQuotaState;
    last24h?: ProviderQuotaState;
    last7d?: ProviderQuotaState;
    total?: ProviderQuotaState;
  };
  status?: string;
  schedulable?: boolean;
  reason?: string;
  tempUnschedulableUntil?: string | null;
  tempUnschedulableReason?: string | null;
  usage?: {
    total?: ProviderUsageWindow;
    last5h?: ProviderUsageWindow;
    last24h?: ProviderUsageWindow;
    last7d?: ProviderUsageWindow;
  };
  lastCall?: {
    status?: string;
    at?: string;
    latencyMs?: number | null;
    inputTokens?: number | null;
    outputTokens?: number | null;
    error?: string | null;
  } | null;
};

type ManageProviderUsagePayload = {
  updatedAt?: string;
  totals?: Record<string, any>;
  providers?: ProviderUsageRow[];
  storage?: Record<string, any>;
};

type SourceUsageWindow = {
  calls?: number;
  okCalls?: number;
  errorCalls?: number;
  rateLimitedCalls?: number;
  skippedCalls?: number;
  resultCount?: number;
  totalLatencyMs?: number;
  avgLatencyMs?: number | null;
};

type SourceUsageRow = {
  source: string;
  enabled?: boolean;
  rps?: number | null;
  rpsPerKey?: number | null;
  concurrency?: number | null;
  apiKeyRefs?: string[];
  keyCount?: number;
  hasConfiguredKey?: boolean;
  email?: string | null;
  tool?: string | null;
  baseUrl?: string | null;
  requiresEmail?: boolean;
  status?: string;
  schedulable?: boolean;
  reason?: string;
  usage?: {
    total?: SourceUsageWindow;
    last24h?: SourceUsageWindow;
    last7d?: SourceUsageWindow;
  };
  lastCall?: {
    query?: string;
    status?: string;
    at?: string;
    latencyMs?: number | null;
    resultCount?: number | null;
    error?: string | null;
  } | null;
};

type ManageSourceUsagePayload = {
  updatedAt?: string;
  totals?: Record<string, any>;
  sources?: SourceUsageRow[];
  storage?: Record<string, any>;
};

type PressurePoint = {
  at: number;
  cpu: number;
  memory: number;
  disk: number;
};

type MetricCardData = {
  label: string;
  value: string;
  footnote: string;
  percent: number;
  tone: 'ok' | 'warn' | 'danger' | 'info';
};

type LoadOptions = {
  silent?: boolean;
  preserve?: boolean;
};

type ProviderKind = 'llm' | 'embedding' | 'rerank';

type ProviderDraft = {
  kind: ProviderKind;
  scope: string;
  model: string;
  baseUrl: string;
  apiKey: string;
  rps: string;
  concurrency: string;
  tokenBudget5h: string;
  tokenBudget24h: string;
  tokenBudget7d: string;
  tokenBudgetTotal: string;
  enabled: boolean;
};

type SourceDraft = {
  source: string;
  baseUrl: string;
  apiKeyRefs: string;
  email: string;
  tool: string;
  rps: string;
  rpsPerKey: string;
  concurrency: string;
  enabled: boolean;
};

type TestEmailDraft = {
  toEmail: string;
  subject: string;
  text: string;
};

const PROVIDER_KIND_LABELS: Record<ProviderKind, string> = {
  llm: 'LLM',
  embedding: 'Embedding',
  rerank: 'Rerank',
};

const EMPTY_PROVIDER_DRAFT: ProviderDraft = {
  kind: 'embedding',
  scope: 'global',
  model: 'bge-m3',
  baseUrl: '',
  apiKey: '',
  rps: '1',
  concurrency: '1',
  tokenBudget5h: '',
  tokenBudget24h: '',
  tokenBudget7d: '',
  tokenBudgetTotal: '',
  enabled: true,
};

const EMPTY_SOURCE_DRAFT: SourceDraft = {
  source: 'openalex',
  baseUrl: '',
  apiKeyRefs: '',
  email: '',
  tool: '',
  rps: '1',
  rpsPerKey: '',
  concurrency: '1',
  enabled: true,
};

const EMPTY_TEST_EMAIL_DRAFT: TestEmailDraft = {
  toEmail: '',
  subject: 'Academic Cluster Resend smoke',
  text: 'Hello from Academic Cluster Edge management console.',
};

const REFRESH_OPTIONS = [
  { label: '5s', value: 5000 },
  { label: '10s', value: 10000 },
  { label: '30s', value: 30000 },
] as const;

function clampPercent(value: unknown): number {
  const number = Number(value);
  if (!Number.isFinite(number)) return 0;
  return Math.max(0, Math.min(100, number));
}
function formatNumber(value: unknown): string {
  const number = Number(value || 0);
  return Number.isFinite(number) ? new Intl.NumberFormat('zh-CN').format(number) : '0';
}

function formatCompactNumber(value: unknown): string {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0';
  return new Intl.NumberFormat('zh-CN', { notation: 'compact', maximumFractionDigits: 1 }).format(number);
}

function formatPercent(value: unknown): string {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return '0%';
  return `${number.toFixed(number >= 10 ? 0 : 1)}%`;
}

function formatBytes(value: unknown): string {
  const bytes = Number(value || 0);
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const digits = unitIndex === 0 ? 0 : size >= 100 ? 0 : size >= 10 ? 1 : 2;
  return `${size.toFixed(digits)} ${units[unitIndex]}`;
}

function formatRange(value: unknown, formatter: (item: unknown) => string = formatNumber): string {
  if (!Array.isArray(value) || value.length === 0) return formatter(value);
  if (value.length === 1 || Number(value[0]) === Number(value[value.length - 1])) return formatter(value[0]);
  return `${formatter(value[0])}-${formatter(value[value.length - 1])}`;
}

function formatDecimal(value: unknown, digits = 1): string {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number.toFixed(digits) : '0.0';
}

function formatMs(value: unknown): string {
  const number = Number(value || 0);
  if (!Number.isFinite(number) || number <= 0) return '0 ms';
  if (number >= 1000) return `${formatDecimal(number / 1000, number >= 10000 ? 1 : 2)} s`;
  if (number < 0.01) return '<0.01 ms';
  return `${formatDecimal(number, number >= 100 ? 0 : number >= 10 ? 1 : 2)} ms`;
}

function formatMeasuredMs(value: unknown, row?: Record<string, any>): string {
  const number = Number(value || 0);
  const hasWork = Number(row?.samples || 0) > 0 || Number(row?.totalInputCount || row?.input_count || 0) > 0;
  if (number <= 0 && hasWork) return '<1 ms';
  return formatMs(value);
}

function formatMoney(cents: unknown, currency: unknown = 'usd'): string {
  const amount = Number(cents || 0) / 100;
  const currencyCode = String(currency || 'usd').toUpperCase();
  if (!Number.isFinite(amount)) return `${currencyCode} 0.00`;
  try {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: currencyCode,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currencyCode} ${amount.toFixed(2)}`;
  }
}

function formatIsoTime(value: unknown): string {
  if (typeof value !== 'string' || !value.trim()) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).format(parsed);
}

function statusClass(status: unknown): string {
  const value = String(status || 'unknown').toLowerCase();
  if (value === 'ok' || value === 'completed' || value === 'paid' || value === 'succeeded' || value === 'delivered' || value === 'sent' || value === 'refund_succeeded') return 'ok';
  if (value === 'running') return 'running';
  if (value === 'queued' || value === 'waiting_worker' || value === 'pending' || value === 'unknown' || value === 'disabled' || value === 'missing_key' || value === 'cooldown' || value === 'checkout_created' || value === 'refunded') return 'queued';
  if (value === 'warn' || value === 'warning' || value === 'degraded' || value === 'rate_limited') return 'warn';
  return 'error';
}

function canRefundStripePayment(payment: ManagePayment): boolean {
  return String(payment.provider || 'stripe').toLowerCase() === 'stripe'
    && String(payment.status || '').toLowerCase() === 'paid'
    && Boolean(String(payment.provider_session_id || '').trim())
    && Boolean(String(payment.provider_payment_id || '').trim())
    && Number(payment.amount_cents || 0) > 0;
}

function pressureTone(value: number, kind: 'cpu' | 'memory' | 'disk'): 'ok' | 'warn' | 'danger' {
  const warn = kind === 'cpu' ? 65 : kind === 'memory' ? 72 : 80;
  const danger = kind === 'cpu' ? 88 : kind === 'memory' ? 86 : 92;
  if (value >= danger) return 'danger';
  if (value >= warn) return 'warn';
  return 'ok';
}

function serviceSubtitle(service: ManageService): string {
  const parts = [service.category, service.mode, service.url || service.backend || service.model].filter(Boolean);
  return parts.map((part) => displayText(part, '')).filter(Boolean).join(' · ');
}

function serviceDetail(service: ManageService): string {
  if (service.error) return displayText(service.error);
  if (service.statusCode) return `HTTP ${service.statusCode}`;
  if (typeof service.latencyMs === 'number') return `latency ${service.latencyMs}ms`;
  if (typeof service.latency_s === 'number') return `latency ${service.latency_s}s`;
  if (typeof service.reply === 'string' && service.reply) return service.reply;
  if (typeof service.nextAction === 'string' && service.nextAction) return service.nextAction;
  if (typeof service.dimensions === 'number') return `${service.dimensions} dims`;
  if (typeof service.results_count === 'number') return `${service.results_count} results`;
  if (typeof service.fallbackReason === 'string' && service.fallbackReason) return service.fallbackReason;
  return 'no details';
}

function serviceMetric(service: ManageService): string {
  if (typeof service.latencyMs === 'number') return `${Math.round(service.latencyMs)}ms`;
  if (typeof service.latency_s === 'number') return `${formatDecimal(service.latency_s, service.latency_s >= 1 ? 1 : 2)}s`;
  if (typeof service.runs === 'number') return `${formatNumber(service.runs)} runs`;
  if (typeof service.dimensions === 'number') return `${formatNumber(service.dimensions)} dim`;
  if (typeof service.results_count === 'number') return `${formatNumber(service.results_count)} results`;
  if (typeof service.concurrency === 'number' && typeof service.rps === 'number') return `${service.concurrency}c / ${formatDecimal(service.rps)}rps`;
  return displayText(service.mode || service.backend, 'runtime');
}

function providerPatchKey(draft: ProviderDraft): string {
  return `${draft.kind}.providers.append`;
}

function providerDraftToConfig(draft: ProviderDraft): Record<string, unknown> {
  const config: Record<string, unknown> = {
    MODEL: draft.model.trim(),
    API_URL: draft.baseUrl.trim(),
    API_KEY: draft.apiKey,
    enabled: draft.enabled,
    RPS: Number(draft.rps) || 1,
    CONCURRENCY: Number(draft.concurrency) || 1,
  };
  if (Number(draft.tokenBudget5h) > 0) config.TOKEN_BUDGET_5H = Math.trunc(Number(draft.tokenBudget5h));
  if (Number(draft.tokenBudget24h) > 0) config.TOKEN_BUDGET_24H = Math.trunc(Number(draft.tokenBudget24h));
  if (Number(draft.tokenBudget7d) > 0) config.TOKEN_BUDGET_7D = Math.trunc(Number(draft.tokenBudget7d));
  if (Number(draft.tokenBudgetTotal) > 0) config.TOKEN_BUDGET_TOTAL = Math.trunc(Number(draft.tokenBudgetTotal));
  return config;
}

function providerDraftToPatch(draft: ProviderDraft): Record<string, unknown> {
  const provider = providerDraftToConfig(draft);
  if (draft.kind === 'llm') {
    provider.BASE_URL = provider.API_URL;
    delete provider.API_URL;
  }
  if (draft.scope.trim() && draft.kind === 'llm' && draft.scope.trim() !== 'global') {
    return { [`llm.${draft.scope.trim()}.providers`]: [provider] };
  }
  return { [providerPatchKey(draft)]: provider };
}

function providersFromConfig(config: Record<string, any> | null, summaryConfig: Record<string, any> | undefined): ManageService[] {
  const summaryProviders = Array.isArray(summaryConfig?.modelProviders) ? summaryConfig?.modelProviders : [];
  if (summaryProviders.length > 0) return summaryProviders as ManageService[];
  const providers: ManageService[] = [];
  const embeddingProviders = config?.embedding?.providers;
  if (Array.isArray(embeddingProviders)) providers.push(...embeddingProviders.map((item: any, index: number) => ({ ...item, kind: 'embedding', scope: 'global', providerIndex: index })));
  const rerankProviders = config?.rerank?.providers;
  if (Array.isArray(rerankProviders)) providers.push(...rerankProviders.map((item: any, index: number) => ({ ...item, kind: 'rerank', scope: 'global', providerIndex: index })));
  const llm = config?.llm || {};
  if (Array.isArray(llm.providers)) providers.push(...llm.providers.map((item: any, index: number) => ({ ...item, kind: 'llm', scope: 'global', providerIndex: index })));
  Object.entries(llm).forEach(([scope, section]) => {
    if (section && typeof section === 'object' && Array.isArray((section as any).providers)) {
      providers.push(...(section as any).providers.map((item: any, index: number) => ({ ...item, kind: 'llm', scope, providerIndex: index })));
    }
  });
  return providers;
}

function buildSparklinePath(values: number[], width = 180, height = 46): string {
  if (values.length === 0) return '';
  if (values.length === 1) {
    const y = height - (clampPercent(values[0]) / 100) * height;
    return `M 0 ${y} L ${width} ${y}`;
  }
  return values.map((value, index) => {
    const x = (index / (values.length - 1)) * width;
    const y = height - (clampPercent(value) / 100) * height;
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');
}

function buildSparklineArea(values: number[], width = 180, height = 46): string {
  const line = buildSparklinePath(values, width, height);
  if (!line) return '';
  return `${line} L ${width} ${height} L 0 ${height} Z`;
}

function getStatusLabel(status: string): string {
  if (status === 'ok' || status === 'completed') return '稳定';
  if (status === 'running') return '处理中';
  if (status === 'queued') return '排队';
  return '异常';
}

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function displayText(value: unknown, fallback = '-'): string {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return fallback;
  }
}

function runIdentity(run: Record<string, any> | null | undefined): string {
  return String(run?.runId || run?.id || run?.run_id || '');
}

function runTitle(run: Record<string, any> | null | undefined): string {
  return displayText(run?.title || run?.topic || runIdentity(run), '(无标题)');
}

function runCreatedAt(run: Record<string, any> | null | undefined): string {
  return String(run?.createdAt || run?.created_at || '');
}

function unwrapRunDetail(detail: Record<string, any> | null): Record<string, any> | null {
  if (!detail) return null;
  return detail.run && typeof detail.run === 'object' ? detail.run : detail;
}

function detailLogs(detail: Record<string, any>): Array<Record<string, any>> {
  if (Array.isArray(detail.logs)) return detail.logs;
  if (Array.isArray(detail.events)) {
    return detail.events.map((event) => ({
      id: event.id,
      message: `[${displayText(event.stage || event.level || 'run', 'run')}] ${displayText(event.message, '')}`,
      createdAt: event.created_at || event.createdAt,
    }));
  }
  return [];
}

function detailArtifacts(detail: Record<string, any>): Array<Record<string, any>> {
  return Array.isArray(detail.artifacts) ? detail.artifacts : [];
}

function artifactHref(artifact: Record<string, any>): string | undefined {
  if (artifact.url) return String(artifact.url);
  if (artifact.id) return resolveApiUrl(`/artifacts/${artifact.id}`);
  return undefined;
}

function artifactLabel(artifact: Record<string, any>): string {
  return displayText(artifact.label || artifact.kind || artifact.r2_key || artifact.id, 'artifact');
}

function detailComputeJobs(detail: Record<string, any>): Array<Record<string, any>> {
  if (Array.isArray(detail.computeJobs)) return detail.computeJobs;
  if (Array.isArray(detail.compute_jobs)) return detail.compute_jobs;
  if (Array.isArray(detail.jobs)) return detail.jobs;
  return [];
}

function detailComputePerformance(detail: Record<string, any>): Record<string, any> {
  const perf = detail.computePerformance || detail.compute_performance || detail.performance;
  return perf && typeof perf === 'object' && !Array.isArray(perf) ? perf : {};
}

function performanceRows(perf: Record<string, any>, key: string): Array<Record<string, any>> {
  const rows = perf?.[key];
  return Array.isArray(rows) ? rows : [];
}

function nestedPerformanceRows(perf: Record<string, any>, section: string, key: string): Array<Record<string, any>> {
  const rows = perf?.[section]?.[key];
  return Array.isArray(rows) ? rows : [];
}

function performanceTotals(perf: Record<string, any>, section: string): Record<string, any> {
  const totals = perf?.[section]?.totals;
  return totals && typeof totals === 'object' && !Array.isArray(totals) ? totals : {};
}

function runNativeStageRows(perf: Record<string, any>): Array<Record<string, any>> {
  return nestedPerformanceRows(perf, 'compute', 'nativeStages');
}

function runOperationRows(perf: Record<string, any>): Array<Record<string, any>> {
  return nestedPerformanceRows(perf, 'compute', 'byOperation');
}

function runProviderRows(perf: Record<string, any>): Array<Record<string, any>> {
  return nestedPerformanceRows(perf, 'providers', 'byTaskProvider');
}

function runSourceRows(perf: Record<string, any>): Array<Record<string, any>> {
  return nestedPerformanceRows(perf, 'sources', 'bySource');
}

function runHeatmapRows(perf: Record<string, any>): Array<Record<string, any>> {
  return Array.isArray(perf?.heatmap) ? perf.heatmap : [];
}

function nativeRuntimeHeatmapRows(perf: Record<string, any>): Array<Record<string, any>> {
  const nativeRows = runNativeStageRows(perf);
  if (nativeRows.length > 0) {
    return nativeRows.map((row) => ({
      category: 'rust/wasm',
      name: `${String(row.operation || 'compute')}/${String(row.stage || 'stage')}`,
      count: row.samples,
      samples: row.samples,
      totalInputCount: row.totalInputCount,
      failed: 0,
      totalMs: Number(row.totalWallMs || 0),
      avgMs: row.avgWallMs,
    }));
  }
  const rustRows = performanceRows(perf, 'rustCandidates');
  if (rustRows.length > 0) {
    return rustRows.map((row) => ({
      category: String(row.engine || 'rust/wasm'),
      name: `${String(row.operation || 'compute')}/${String(row.stage || 'stage')}`,
      count: row.count || row.samples || 1,
      samples: row.samples || row.count || 1,
      totalInputCount: row.input_count || row.inputCount,
      failed: row.errors || 0,
      totalMs: Number(row.process_cpu_ms ?? row.duration_ms ?? 0),
      avgMs: row.process_cpu_ms ?? row.duration_ms ?? null,
    }));
  }
  return [];
}

function hasRunPerformance(perf: Record<string, any>): boolean {
  return runNativeStageRows(perf).length > 0
    || runOperationRows(perf).length > 0
    || runProviderRows(perf).length > 0
    || runSourceRows(perf).length > 0
    || runHeatmapRows(perf).length > 0
    || nativeRuntimeHeatmapRows(perf).length > 0
    || performanceRows(perf, 'rustCandidates').length > 0
    || performanceRows(perf, 'externalWait').length > 0;
}

function normalizeRunDetail(detail: Record<string, any> | null) {
  const run = unwrapRunDetail(detail);
  if (!detail || !run) return null;
  const events = detailLogs(detail);
  const latestErrorEvent = [...events].reverse().find((event) => String(event.message || '').trim() && String(event.message || '').toLowerCase().includes('failed'));
  const computeJobs = detailComputeJobs(detail);
  const computePerformance = detailComputePerformance(detail);
  return {
    run,
    id: runIdentity(run),
    title: runTitle(run),
    status: String(run.status || 'unknown'),
    createdAt: runCreatedAt(run),
    updatedAt: String(run.updatedAt || run.updated_at || ''),
    currentStage: String(run.currentStage || run.current_step || '-'),
    tokenUsage: Number(run.tokenUsage ?? run.totalTokens ?? 0),
    calls: Number(run.calls ?? run.totalCalls ?? 0),
    stages: Array.isArray(detail.stages) ? detail.stages : [],
    literature: Array.isArray(detail.literature) ? detail.literature : [],
    artifacts: detailArtifacts(detail),
    logs: events,
    latestErrorEvent,
    computeJobs,
    computePerformance,
    error: run.error || detail.error,
    workflowInstanceId: run.workflowInstanceId || run.workflow_instance_id,
    credits: {
      reserved: run.reservedCredits ?? run.reserved_credits,
      actual: run.actualCredits ?? run.actual_credits,
    },
  };
}

function summaryServices(summary: ManageSummary | null): ManageService[] {
  const direct = summary?.services;
  if (Array.isArray(direct)) return direct;
  const providers = (summary as any)?.providers?.providers;
  const sources = (summary as any)?.sources?.sources;
  const providerServices = Array.isArray(providers)
    ? providers.map((provider: any) => ({
      ...provider,
      name: provider.id || provider.provider || provider.model || 'provider',
      status: provider.enabled === false ? 'queued' : 'ok',
      mode: 'remote',
      category: provider.taskType || 'provider',
    }))
    : [];
  const sourceServices = Array.isArray(sources)
    ? sources.map((source: any) => ({
      ...source,
      name: source.source,
      status: Array.isArray((summary as any)?.sources?.issues) && (summary as any).sources.issues.length > 0 ? 'queued' : 'ok',
      mode: 'remote',
      category: 'source',
    }))
    : [];
  return [...providerServices, ...sourceServices];
}

function providerWindow(row: ProviderUsageRow, key: 'total' | 'last5h' | 'last24h' | 'last7d'): ProviderUsageWindow {
  return row.usage?.[key] || {};
}

function providerQuotaText(quota: ProviderQuotaState | undefined): string {
  if (!quota || !quota.limit) return 'unlimited';
  return `${formatCompactNumber(quota.used)} / ${formatCompactNumber(quota.limit)}`;
}

function providerQuotaPercent(quota: ProviderQuotaState | undefined): number {
  if (!quota || !quota.limit) return 0;
  return clampPercent((Number(quota.used || 0) / Number(quota.limit)) * 100);
}

function providerErrorRate(window: ProviderUsageWindow): number {
  const calls = Number(window.calls || 0);
  if (calls <= 0) return 0;
  return (Number(window.errorCalls || 0) / calls) * 100;
}

function providerStatus(row: ProviderUsageRow): string {
  if (row.enabled === false) return 'disabled';
  if (row.hasApiKey === false) return 'missing_key';
  if (row.status) return String(row.status);
  return row.lastCall?.status === 'error' ? 'degraded' : 'ok';
}

function providerRowTone(row: ProviderUsageRow): string {
  const status = providerStatus(row);
  if (status === 'ok') return 'ok';
  if (status === 'disabled' || status === 'missing_key' || status === 'orphaned' || status === 'cooldown') return 'queued';
  if (status === 'quota_exhausted') return 'error';
  return 'error';
}

function freePlanTone(row: Record<string, any>): 'ok' | 'warn' | 'danger' | 'info' {
  const status = String(row.status || '').toLowerCase();
  if (status === 'exceeded') return 'danger';
  if (status === 'warn') return 'warn';
  if (status === 'platform' || row.limit == null) return 'info';
  return 'ok';
}

function freePlanValue(row: Record<string, any>, key: 'used' | 'limit' | 'remaining'): string {
  const value = row[key];
  if (value == null) return '平台侧';
  return String(row.unit || '').includes('bytes') ? formatBytes(value) : formatCompactNumber(value);
}

function sourceWindow(row: SourceUsageRow, key: 'total' | 'last24h' | 'last7d'): SourceUsageWindow {
  return row.usage?.[key] || {};
}

function sourceErrorRate(window: SourceUsageWindow): number {
  const calls = Number(window.calls || 0);
  if (calls <= 0) return 0;
  return ((Number(window.errorCalls || 0) + Number(window.rateLimitedCalls || 0)) / calls) * 100;
}

function sourceStatus(row: SourceUsageRow): string {
  if (row.enabled === false) return 'disabled';
  if (row.status) return String(row.status);
  return row.lastCall?.status === 'error' ? 'degraded' : 'ok';
}

function sourceRowTone(row: SourceUsageRow): string {
  const status = sourceStatus(row);
  if (status === 'ok') return 'ok';
  if (status === 'disabled' || status === 'missing_key' || status === 'missing_email' || status === 'orphaned') return 'queued';
  if (status === 'rate_limited') return 'warn';
  return 'error';
}

function runtimeHeatTone(value: number, max: number): string {
  if (max <= 0) return 'heat-0';
  const ratio = value / max;
  if (ratio >= 0.75) return 'heat-4';
  if (ratio >= 0.45) return 'heat-3';
  if (ratio >= 0.2) return 'heat-2';
  if (ratio > 0) return 'heat-1';
  return 'heat-0';
}

function externalWaitCategory(row: Record<string, any>): 'source' | 'rerank' | 'llm' | 'embedding' {
  const raw = String(row.category || row.taskType || row.engine || row.kind || '').toLowerCase();
  if (raw.includes('source') || row.source) return 'source';
  if (raw.includes('rerank')) return 'rerank';
  if (raw.includes('embed')) return 'embedding';
  return 'llm';
}

function externalWaitRows(perf: Record<string, any>): Array<Record<string, any>> {
  const timeline = performanceRows(perf, 'externalWait');
  if (timeline.length > 0) return timeline.map((row) => ({
    ...row,
    category: externalWaitCategory(row),
    name: row.name || row.source || row.provider || row.model || row.operation || row.stage || 'wait',
    latencyMs: Number(row.latencyMs ?? row.latency_ms ?? row.duration_ms ?? row.totalLatencyMs ?? 0),
  }));
  const fallbackRows: Array<Record<string, any>> = [
    ...runSourceRows(perf).map((row) => ({
      ...row,
      category: 'source',
      name: row.source || 'source',
      latencyMs: Number(row.totalLatencyMs || 0),
    })),
    ...runProviderRows(perf).map((row) => ({
      ...row,
      category: externalWaitCategory(row),
      name: `${String(row.provider || row.taskType || 'provider')}${row.model ? `/${String(row.model)}` : ''}`,
      latencyMs: Number(row.totalLatencyMs || 0),
    })),
  ];
  return fallbackRows.sort((a, b) => String(a.at || '').localeCompare(String(b.at || '')));
}

function artifactKindMeta(kind: unknown): { label: string; tone: string } {
  const value = String(kind || '').toLowerCase();
  if (value.includes('review')) return { label: 'Review', tone: 'review' };
  if (value.includes('bib')) return { label: 'BibTeX', tone: 'bib' };
  if (value.includes('citation')) return { label: 'Citations', tone: 'data' };
  if (value.includes('snapshot')) return { label: 'Snapshot', tone: 'snapshot' };
  return { label: 'Artifact', tone: 'data' };
}

function PressureTile(props: {
  label: string;
  value: number;
  unit: string;
  tone: 'ok' | 'warn' | 'danger';
  detail: string;
  history: number[];
}) {
  const { label, value, unit, tone, detail, history } = props;
  const linePath = buildSparklinePath(history);
  const areaPath = buildSparklineArea(history);

  return (
    <article className={`manage-pressure-tile tone-${tone}`}>
      <div className="manage-pressure-topline">
        <span>{label}</span>
        <small>{getStatusLabel(tone)}</small>
      </div>
      <div className="manage-pressure-reading">
        <strong>{formatDecimal(value)}{unit}</strong>
        <p>{detail}</p>
      </div>
      <div className="manage-pressure-chart" aria-hidden="true">
        <svg viewBox="0 0 180 46" preserveAspectRatio="none" className="manage-sparkline">
          <path className="manage-sparkline-area" d={areaPath} />
          <path className="manage-sparkline-line" d={linePath} />
        </svg>
      </div>
      <div className="manage-pressure-bar">
        <i style={{ width: `${clampPercent(value)}%` }} />
      </div>
    </article>
  );
}

function MetricCard({ card }: { card: MetricCardData }) {
  return (
    <div className={`manage-metric-card tone-${card.tone}`}>
      <span>{card.label}</span>
      <strong>{card.value}</strong>
      <div className="manage-metric-bar" aria-hidden="true">
        <i style={{ width: `${card.percent}%` }} />
      </div>
      <small>{card.footnote}</small>
    </div>
  );
}

function StageLane({ stage, loadPercent }: { stage: ManageStageBucket; loadPercent: number }) {
  const runs = Array.isArray(stage.runs) ? stage.runs.slice(0, 3) : [];
  return (
    <article className="manage-stage-lane">
      <div className="manage-stage-lane-head">
        <div>
          <h3>{displayText(stage.label, 'Unnamed Stage')}</h3>
          <p>运行 {formatNumber(stage.running)} · 排队 {formatNumber(stage.queued)}</p>
        </div>
        <strong>{Math.round(loadPercent)}%</strong>
      </div>
      <div className="manage-stage-bar manage-stage-bar-wide">
        <i style={{ width: `${loadPercent}%` }} />
      </div>
      <div className="manage-stage-run-chips">
        {runs.length > 0 ? runs.map((run) => (
          <span key={`${run.runId}-${run.queueState}`} className="manage-stage-run-chip">
            {run.queueState === 'running' ? 'RUN' : 'Q'} · {String(run.topic || run.runId || 'task')}
          </span>
        )) : <span className="manage-empty-inline">当前无活跃任务</span>}
      </div>
    </article>
  );
}

function ServiceCard({ service }: { service: ManageService }) {
  return (
    <article className={`manage-service-card tone-${statusClass(service.status)}`}>
      <div className="manage-service-card-head">
        <div>
          <span className={`manage-status ${statusClass(service.status)}`}>{String(service.status || 'unknown')}</span>
          <h3>{displayText(service.name, 'service')}</h3>
        </div>
        <strong>{serviceMetric(service)}</strong>
      </div>
      <p className="manage-service-subtitle">{serviceSubtitle(service) || 'runtime endpoint'}</p>
      <p className="manage-service-detail">{serviceDetail(service)}</p>
      <div className="manage-service-meta">
        {service.model && <span>{displayText(service.model)}</span>}
        {service.backend && <span>{displayText(service.backend)}</span>}
        {typeof service.redisReachable === 'boolean' && <span>{service.redisReachable ? 'Redis reachable' : 'Redis offline'}</span>}
        {typeof service.autoSwitched === 'boolean' && service.autoSwitched && <span>auto switched</span>}
        {typeof service.rps === 'number' && <span>{formatDecimal(service.rps)} rps</span>}
        {typeof service.concurrency === 'number' && <span>{formatNumber(service.concurrency)} concurrency</span>}
        {service.scope && <span>{displayText(service.scope)}</span>}
        {service.deploymentUnit && <span>{displayText(service.deploymentUnit)}</span>}
        {service.configurationHome && <span>{displayText(service.configurationHome)}</span>}
        {service.secretPlacement && <span>{displayText(service.secretPlacement)}</span>}
        {typeof service.dashboardConfigurable === 'boolean' && (
          <span>{service.dashboardConfigurable ? '后台可配置' : '平台侧配置'}</span>
        )}
      </div>
      {(service.nextAction || service.docUrl) && (
        <div className="manage-service-next">
          {service.nextAction && <code>{displayText(service.nextAction)}</code>}
          {service.docUrl && (
            <a href={String(service.docUrl)} target="_blank" rel="noreferrer">
              官方文档
            </a>
          )}
        </div>
      )}
    </article>
  );
}

function ProviderQuotaBars(props: { row: ProviderUsageRow }) {
  const { row } = props;
  const recent = providerWindow(row, 'last5h');
  const day = providerWindow(row, 'last24h');
  const week = providerWindow(row, 'last7d');
  const total = providerWindow(row, 'total');
  const bars = [
    { label: '5h', value: providerQuotaPercent(row.quota?.last5h), text: providerQuotaText(row.quota?.last5h), calls: recent.calls, tokens: recent.totalTokens },
    { label: '24h', value: providerQuotaPercent(row.quota?.last24h), text: providerQuotaText(row.quota?.last24h), calls: day.calls, tokens: day.totalTokens },
    { label: '7d', value: providerQuotaPercent(row.quota?.last7d), text: providerQuotaText(row.quota?.last7d), calls: week.calls, tokens: week.totalTokens },
    { label: 'Total', value: providerQuotaPercent(row.quota?.total), text: providerQuotaText(row.quota?.total), calls: total.calls, tokens: total.totalTokens },
  ];
  return (
    <div className="manage-quota-bars">
      {bars.map((bar) => (
        <div className="manage-quota-row" key={`${row.id}-${bar.label}`}>
          <span>{bar.label}</span>
          <div className="manage-quota-track" aria-label={`${bar.label} ${bar.text}`}>
            <i style={{ width: bar.text === 'unlimited' ? '100%' : `${bar.value}%` }} />
          </div>
          <strong>{bar.text}</strong>
          <small>{formatNumber(bar.calls)} calls · {formatCompactNumber(bar.tokens)} tok</small>
        </div>
      ))}
    </div>
  );
}

function RuntimeHeatmapGrid(props: { rows: Array<Record<string, any>> }) {
  const rows = props.rows.slice(0, 84);
  const maxMs = rows.reduce((max, row) => Math.max(max, Number(row.totalMs || row.latencyMs || 0)), 0);
  if (rows.length === 0) {
    return <div className="manage-empty-visual">暂无热力图数据</div>;
  }
  return (
    <div className="manage-runtime-heatmap">
      <div className="manage-heatmap-grid">
        {rows.map((row, index) => {
          const totalMs = Number(row.totalMs || row.latencyMs || 0);
          const title = `${String(row.category || 'runtime')} ${String(row.name || index)} ${formatMs(totalMs)}`;
          return (
            <span
              key={`${row.category}-${row.name}-${index}`}
              className={`manage-heat-cell ${runtimeHeatTone(totalMs, maxMs)}`}
              title={title}
              aria-label={title}
            />
          );
        })}
      </div>
      <div className="manage-heatmap-legend">
        <span>少</span><i className="manage-heat-cell heat-1" /><i className="manage-heat-cell heat-2" /><i className="manage-heat-cell heat-3" /><i className="manage-heat-cell heat-4" /><span>多</span>
      </div>
      <div className="manage-heatmap-top">
        {rows.slice(0, 8).map((row, index) => (
          <div key={`${row.category}-${row.name}-top-${index}`}>
            <span>{String(row.category || 'runtime')}</span>
            <strong>{String(row.name || 'metric')}</strong>
            <small>{formatMeasuredMs(row.totalMs, row)} · avg {formatMeasuredMs(row.avgMs, row)} · {formatNumber(row.count)}x</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExternalWaitTimeline(props: { rows: Array<Record<string, any>> }) {
  const rows = props.rows.slice(0, 80);
  const maxMs = rows.reduce((max, row) => Math.max(max, Number(row.latencyMs || row.totalLatencyMs || row.duration_ms || 0)), 0);
  if (rows.length === 0) {
    return <div className="manage-empty-visual">暂无外部等待记录</div>;
  }
  return (
    <div className="manage-wait-timeline">
      <div className="manage-wait-bars">
        {rows.map((row, index) => {
          const latencyMs = Number(row.latencyMs || row.totalLatencyMs || row.duration_ms || 0);
          const category = externalWaitCategory(row);
          const height = maxMs > 0 ? Math.max(12, (latencyMs / maxMs) * 116) : 12;
          const title = `${category} ${String(row.name || row.source || row.provider || 'wait')} ${formatMs(latencyMs)}`;
          return (
            <span
              key={`${category}-${row.name || index}-${row.at || index}`}
              className={`manage-wait-bar wait-${category}`}
              style={{ height }}
              title={title}
              aria-label={title}
            />
          );
        })}
      </div>
      <div className="manage-wait-legend">
        {(['source', 'rerank', 'llm', 'embedding'] as const).map((item) => <span key={item}><i className={`wait-${item}`} />{item}</span>)}
      </div>
      <div className="manage-wait-table">
        {rows.slice(-8).map((row, index) => {
          const category = externalWaitCategory(row);
          const latencyMs = Number(row.latencyMs || row.totalLatencyMs || row.duration_ms || 0);
          return (
            <div key={`${category}-${row.name}-${index}`}>
              <span className={`manage-status ${statusClass(row.status || (row.failed ? 'failed' : 'ok'))}`}>{category}</span>
              <strong>{String(row.name || row.source || row.provider || 'wait')}</strong>
              <small>{formatIsoTime(row.at)} · {formatMs(latencyMs)}{row.tokens != null ? ` · ${formatCompactNumber(row.tokens)} tok` : ''}{row.results != null ? ` · ${formatNumber(row.results)} results` : ''}</small>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NativeRuntimeStrip(props: { rows: Array<Record<string, any>> }) {
  const rows = props.rows.slice(0, 10);
  const maxMs = rows.reduce((max, row) => Math.max(max, Number(row.totalWallMs || row.duration_ms || 0)), 0);
  if (rows.length === 0) {
    return <div className="manage-empty-visual">暂无 Rust/WASM 自报耗时</div>;
  }
  return (
    <div className="manage-native-strip">
      {rows.map((row) => {
        const totalMs = Number(row.totalWallMs || row.duration_ms || 0);
        const width = maxMs > 0 ? clampPercent((totalMs / maxMs) * 100) : 0;
        return (
          <div key={`${row.operation}-${row.stage}-native-strip`}>
            <div>
              <strong>{String(row.operation || 'compute')} / {String(row.stage || 'stage')}</strong>
              <small>实际 {formatMeasuredMs(totalMs, row)} · avg {formatMeasuredMs(row.avgWallMs, row)} · samples {formatNumber(row.samples)}</small>
            </div>
            <span><i style={{ width: `${width}%` }} /></span>
          </div>
        );
      })}
    </div>
  );
}

function ArtifactGrid(props: { artifacts: Array<Record<string, any>> }) {
  if (props.artifacts.length === 0) {
    return <div className="manage-empty-visual">暂无产物</div>;
  }
  return (
    <div className="manage-artifact-grid">
      {props.artifacts.map((artifact) => {
        const href = artifactHref(artifact);
        const meta = artifactKindMeta(artifact.kind || artifact.type || artifact.label);
        const body = (
          <>
            <span className={`manage-artifact-badge artifact-${meta.tone}`}>{meta.label}</span>
            <strong>{artifactLabel(artifact)}</strong>
            <small>{formatBytes(artifact.size_bytes || artifact.sizeBytes)} · {formatIsoTime(artifact.created_at || artifact.createdAt)}</small>
          </>
        );
        return href
          ? <a className="manage-artifact-card" key={href} href={href} target="_blank" rel="noreferrer">{body}</a>
          : <div className="manage-artifact-card" key={artifactLabel(artifact)}>{body}</div>;
      })}
    </div>
  );
}

function DeploymentStepCard({ step }: { step: Record<string, any> }) {
  const status = String(step.status || 'unknown');
  const docs = Array.isArray(step.docs) ? step.docs : [];
  const secrets = Array.isArray(step.secretKeys) ? step.secretKeys : [];
  const publicVars = Array.isArray(step.publicVars) ? step.publicVars : [];
  return (
    <article className={`manage-deploy-step tone-${statusClass(status)}`}>
      <div className="manage-deploy-step-head">
        <div>
          <span className={`manage-status ${statusClass(status)}`}>{status}</span>
          <h3>{String(step.title || step.id || 'deployment step')}</h3>
        </div>
        <strong>{String(step.owner || 'runtime')}</strong>
      </div>
      <p className="manage-service-subtitle">{String(step.target || '-')}</p>
      <p className="manage-service-detail">{String(step.evidence || step.nextAction || '-')}</p>
      {step.command && <code className="manage-deploy-command">{String(step.command)}</code>}
      <div className="manage-service-meta">
        <span>{step.canConfigureInDashboard ? '后台可配置' : '平台侧配置'}</span>
        {step.requiredForFirstPageLogin && <span>首屏必需</span>}
        {step.requiredForProduction && <span>生产必需</span>}
        {secrets.length > 0 && <span>secrets {secrets.length}</span>}
        {publicVars.length > 0 && <span>public vars {publicVars.length}</span>}
      </div>
      {step.nextAction && <small className="manage-deploy-next">{String(step.nextAction)}</small>}
      {docs.length > 0 && (
        <div className="manage-service-next">
          <code>{String(step.id || 'docs')}</code>
          <a href={String(docs[0])} target="_blank" rel="noreferrer">官方文档</a>
        </div>
      )}
    </article>
  );
}

function FreePlanUsagePanel(props: { usage: ManageFreePlanUsage | null | undefined }) {
  const usage = props.usage;
  const rows = Array.isArray(usage?.dimensions) ? usage.dimensions : [];
  const basis = usage?.basis || {};
  return (
    <div className="manage-card manage-card-deep">
      <div className="manage-panel-head">
        <div>
          <p className="manage-panel-kicker">Cloudflare Free</p>
          <h3>Free 计划额度</h3>
        </div>
        <span className="manage-panel-meta">
          今日 {formatNumber(basis.todayRuns || 0)} runs · 还可估算 {formatNumber(basis.remainingSafeRunsToday || 0)}
        </span>
      </div>
      <div className="manage-free-plan-grid">
        {rows.map((row) => {
          const tone = freePlanTone(row);
          const percent = row.percent == null ? 0 : clampPercent(row.percent);
          return (
            <article className={`manage-free-plan-card tone-${tone}`} key={String(row.id || row.label)}>
              <div className="manage-free-plan-head">
                <span className={`manage-status ${tone === 'danger' ? 'error' : tone}`}>{String(row.source || 'local')}</span>
                <strong>{String(row.label || row.id)}</strong>
              </div>
              <div className="manage-free-plan-meter">
                <i style={{ width: row.limit == null ? '100%' : `${percent}%` }} />
              </div>
              <div className="manage-free-plan-values">
                <span>已用 <strong>{freePlanValue(row, 'used')}</strong></span>
                <span>剩余 <strong>{freePlanValue(row, 'remaining')}</strong></span>
                <span>上限 <strong>{freePlanValue(row, 'limit')}</strong></span>
              </div>
              <small>{String(row.note || '')}</small>
            </article>
          );
        })}
        {rows.length === 0 && <div className="manage-empty-visual">暂无 Free 计划额度数据</div>}
      </div>
      {Array.isArray(usage?.notes) && usage.notes.length > 0 && (
        <div className="manage-free-plan-notes">
          {usage.notes.map((note) => <span key={displayText(note)}>{displayText(note)}</span>)}
        </div>
      )}
    </div>
  );
}

function ProviderConfigPanel(props: {
  providers: ManageService[];
  usage: ManageProviderUsagePayload | null;
  draft: ProviderDraft;
  setDraft: (draft: ProviderDraft) => void;
  onSave: () => void;
  onTest: () => void;
  onRefreshUsage: () => void;
  onToggleProvider: (providerId: string, enabled: boolean) => void;
  onClearProviderCooldown: (providerId: string) => void;
  onDeleteProvider: (providerId: string) => void;
  onTestProvider: (row: ProviderUsageRow) => void;
  testResult: Record<string, any> | null;
  testError: string;
  testingProviderId: string;
  busy: boolean;
  usageRefreshing: boolean;
}) {
  const { providers, usage, draft, setDraft, onSave, onTest, onRefreshUsage, onToggleProvider, onClearProviderCooldown, onDeleteProvider, onTestProvider, testResult, testError, testingProviderId, busy, usageRefreshing } = props;
  const grouped = providers.reduce<Record<string, ManageService[]>>((acc, provider) => {
    const kind = String(provider.kind || 'model');
    acc[kind] = [...(acc[kind] || []), provider];
    return acc;
  }, {});
  const usageRows = Array.isArray(usage?.providers) ? usage.providers : [];
  const usageGrouped = usageRows.reduce<Record<string, ProviderUsageRow[]>>((acc, row) => {
    const kind = String(row.taskType || 'model');
    acc[kind] = [...(acc[kind] || []), row];
    return acc;
  }, {});
  const totals = usage?.totals || {};

  return (
    <div className="manage-provider-console">
      <div className="manage-card manage-provider-ops-card manage-card-deep">
        <div className="manage-panel-head">
          <div>
            <p className="manage-panel-kicker">Provider Ops</p>
            <h3>模型 Key 状态与 Token 消耗</h3>
          </div>
          <div className="manage-actions">
            <span className="manage-panel-meta">
              {formatNumber(totals.schedulable)} / {formatNumber(totals.providers)} schedulable
            </span>
            <button className="manage-secondary" type="button" onClick={onRefreshUsage} disabled={busy || usageRefreshing}>
              {usageRefreshing ? '刷新中' : '刷新状态'}
            </button>
          </div>
        </div>
        <div className="manage-provider-ops-summary">
          <div><span>5h Calls</span><strong>{formatNumber(totals.calls5h)}</strong><small>{formatCompactNumber(totals.tokens5h)} tokens</small></div>
          <div><span>24h Calls</span><strong>{formatNumber(totals.calls24h)}</strong><small>{formatCompactNumber(totals.tokens24h)} tokens</small></div>
          <div><span>7d Calls</span><strong>{formatNumber(totals.calls7d)}</strong><small>{formatCompactNumber(totals.tokens7d)} tokens</small></div>
          <div><span>Total Calls</span><strong>{formatNumber(totals.calls)}</strong><small>{formatCompactNumber(totals.tokens)} tokens</small></div>
          <div><span>5h Errors</span><strong>{formatNumber(totals.errors5h)}</strong><small>7d {formatNumber(totals.errors7d)}</small></div>
        </div>
        <div className="manage-provider-ops-grid">
          {(['llm', 'embedding', 'rerank'] as ProviderKind[]).map((kind) => {
            const rows = usageGrouped[kind] || [];
            return (
              <article className="manage-provider-ops-column" key={`usage-${kind}`}>
                <div className="manage-provider-column-head">
                  <span>{PROVIDER_KIND_LABELS[kind]}</span>
                  <strong>{formatNumber(rows.length)}</strong>
                </div>
                <div className="manage-provider-ops-list">
                  {rows.length > 0 ? rows.map((row) => {
                    const recent = providerWindow(row, 'last5h');
                    const total = providerWindow(row, 'total');
                    const status = providerStatus(row);
                    const orphaned = row.status === 'orphaned';
                    return (
                      <div className="manage-provider-ops-row" key={`${kind}-${row.id}`}>
                        <div className="manage-provider-ops-row-head">
                          <div>
                            <span className={`manage-status ${providerRowTone(row)}`}>{status}</span>
                            <strong>{displayText(row.model || row.id)}</strong>
                            <small>{displayText(row.id)} · {displayText(row.baseUrl, 'no baseUrl')}</small>
                          </div>
                          <div className="manage-provider-row-actions">
                            {orphaned && <span className="manage-provider-history-pill">历史记录</span>}
                            {!orphaned && (
                              <>
                                <button
                                  className="manage-secondary"
                                  type="button"
                                  onClick={() => onTestProvider(row)}
                                  disabled={busy || row.enabled === false || !row.taskType || testingProviderId === row.id}
                                >
                                  {testingProviderId === row.id ? '测试中' : '测试'}
                                </button>
                                <button
                                  className={row.enabled === false ? 'manage-primary' : 'manage-secondary'}
                                  type="button"
                                  onClick={() => onToggleProvider(row.id, row.enabled === false)}
                                  disabled={busy}
                                >
                                  {row.enabled === false ? '启用' : '停用'}
                                </button>
                                <button
                                  className="manage-danger"
                                  type="button"
                                  onClick={() => onDeleteProvider(row.id)}
                                  disabled={busy}
                                >
                                  删除
                                </button>
                              </>
                            )}
                            {orphaned && (
                              <button
                                className="manage-danger"
                                type="button"
                                onClick={() => onDeleteProvider(row.id)}
                                disabled={busy}
                              >
                                清理
                              </button>
                            )}
                          </div>
                          {row.tempUnschedulableUntil && Date.parse(row.tempUnschedulableUntil) > Date.now() && (
                            <button
                              className="manage-secondary"
                              type="button"
                              onClick={() => onClearProviderCooldown(row.id)}
                              disabled={busy || row.status === 'orphaned'}
                            >
                              清除冷却
                            </button>
                          )}
                        </div>
                        <div className="manage-provider-ops-metrics">
                          <span>5h {formatNumber(recent.calls)} calls</span>
                          <span>{formatCompactNumber(recent.totalTokens)} tokens</span>
                          <span>{formatPercent(providerErrorRate(recent))} err</span>
                          <span>{formatMs(recent.avgLatencyMs)}</span>
                        </div>
                        <ProviderQuotaBars row={row} />
                        <div className="manage-provider-keyline">
                          <span className={`manage-status ${row.hasApiKey ? 'ok' : 'error'}`}>key {row.hasApiKey ? 'ok' : 'missing'}</span>
                          <small>{formatNumber(total.calls)} total calls · {formatCompactNumber(total.totalTokens)} all tokens</small>
                        </div>
                        <small className="manage-provider-ops-footnote">
                          last {row.lastCall ? `${String(row.lastCall.status || '-')} · ${formatIsoTime(row.lastCall.at)} · ${formatMs(row.lastCall.latencyMs)}` : 'no call'}
                          {row.lastCall?.error ? ` · ${String(row.lastCall.error).slice(0, 120)}` : ''}
                          {row.tempUnschedulableUntil ? ` · cooldown ${formatIsoTime(row.tempUnschedulableUntil)}` : ''}
                          {row.tempUnschedulableReason ? ` · ${String(row.tempUnschedulableReason).slice(0, 120)}` : ''}
                          {' · '}{row.reason || 'ready'}
                        </small>
                      </div>
                    );
                  }) : <p className="manage-muted">暂无用量。保存 provider 或执行模型调用后会出现。</p>}
                </div>
              </article>
            );
          })}
        </div>
        <p className="manage-muted">
          运行时配置存 D1 system_config.provider_pool；API Key 以密文或 secret 引用保存；调用记录存 D1 provider_calls。后台保存或启停后，Worker 下一次 provider 调用会重新读取 D1 配置。
        </p>
      </div>

      <div className="manage-provider-board">
        {(['llm', 'embedding', 'rerank'] as ProviderKind[]).map((kind) => {
          const items = grouped[kind] || [];
          return (
            <article className="manage-provider-column" key={kind}>
              <div className="manage-provider-column-head">
                <span>{PROVIDER_KIND_LABELS[kind]}</span>
                <strong>{formatNumber(items.length)}</strong>
              </div>
              <div className="manage-provider-list">
                {items.length > 0 ? items.map((provider, index) => (
                  <div className="manage-provider-row" key={`${kind}-${provider.scope || 'global'}-${provider.base_url || provider.baseUrl || provider.API_URL || index}`}>
                    <div>
                      <span className={`manage-status ${provider.enabled === false ? 'queued' : 'ok'}`}>{provider.enabled === false ? 'disabled' : 'enabled'}</span>
                      <strong>{displayText(provider.model || provider.MODEL, '未配置模型')}</strong>
                      <small>{displayText(provider.scope, 'global')} · {displayText(provider.endpoint || provider.base_url || provider.baseUrl || provider.API_URL, '未设置地址')}</small>
                    </div>
                    <em>{formatDecimal(provider.rps || provider.RPS || 1)} rps / {formatNumber(provider.concurrency || provider.CONCURRENCY || 1)}c</em>
                  </div>
                )) : <p className="manage-muted">暂无 provider，保存后会进入全局池。</p>}
              </div>
            </article>
          );
        })}
      </div>

      <div className="manage-provider-editor">
        <div className="manage-panel-head">
          <div>
            <p className="manage-panel-kicker">Provider Factory</p>
            <h3>添加模型服务节点</h3>
          </div>
          <span className="manage-panel-meta">自动轮询 · 并发分流 · RPS 限流</span>
        </div>
        <div className="manage-provider-form-grid">
          <label><span>类型</span><select className="manage-input" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value as ProviderKind, model: e.target.value === 'embedding' ? 'bge-m3' : e.target.value === 'rerank' ? 'bge-reranker-large' : draft.model })}>{(['llm', 'embedding', 'rerank'] as ProviderKind[]).map((kind) => <option key={kind} value={kind}>{PROVIDER_KIND_LABELS[kind]}</option>)}</select></label>
          <label><span>LLM agent/scope</span><input className="manage-input" value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value })} placeholder="global / review_writer / data_analyst" disabled={draft.kind !== 'llm'} /></label>
          <label><span>模型</span><input className="manage-input" value={draft.model} onChange={(e) => setDraft({ ...draft, model: e.target.value })} placeholder="bge-m3 / DeepSeek-V4-Flash" /></label>
          <label><span>URL</span><input className="manage-input" value={draft.baseUrl} onChange={(e) => setDraft({ ...draft, baseUrl: e.target.value })} placeholder="http://host:port 或 .../v1" /></label>
          <label><span>API Key</span><input className="manage-input" value={draft.apiKey} onChange={(e) => setDraft({ ...draft, apiKey: e.target.value })} placeholder="保存后加密写入 D1，显示时脱敏" type="password" /></label>
          <label><span>RPS</span><input className="manage-input" value={draft.rps} onChange={(e) => setDraft({ ...draft, rps: e.target.value })} inputMode="decimal" /></label>
          <label><span>并发</span><input className="manage-input" value={draft.concurrency} onChange={(e) => setDraft({ ...draft, concurrency: e.target.value })} inputMode="numeric" /></label>
          <label><span>5h token 上限</span><input className="manage-input" value={draft.tokenBudget5h} onChange={(e) => setDraft({ ...draft, tokenBudget5h: e.target.value })} placeholder="留空为不限" inputMode="numeric" /></label>
          <label><span>24h token 上限</span><input className="manage-input" value={draft.tokenBudget24h} onChange={(e) => setDraft({ ...draft, tokenBudget24h: e.target.value })} placeholder="留空为不限" inputMode="numeric" /></label>
          <label><span>7d token 上限</span><input className="manage-input" value={draft.tokenBudget7d} onChange={(e) => setDraft({ ...draft, tokenBudget7d: e.target.value })} placeholder="留空为不限" inputMode="numeric" /></label>
          <label><span>总 token 上限</span><input className="manage-input" value={draft.tokenBudgetTotal} onChange={(e) => setDraft({ ...draft, tokenBudgetTotal: e.target.value })} placeholder="留空为不限" inputMode="numeric" /></label>
          <label className="manage-provider-check"><input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> 启用</label>
        </div>
        <div className="manage-actions">
          <button className="manage-primary" onClick={onSave} disabled={busy || !draft.model.trim() || !draft.baseUrl.trim()}>保存 provider</button>
          <button className="manage-secondary" onClick={onTest} disabled={busy || !draft.model.trim() || !draft.baseUrl.trim()}>先测试</button>
        </div>
        {testError && <div className="manage-inline-error">Provider 测试失败：{testError}</div>}
        {testResult && <pre className="manage-code manage-provider-test-result">{JSON.stringify(testResult, null, 2)}</pre>}
      </div>
    </div>
  );
}

function SourceOpsPanel(props: {
  usage: ManageSourceUsagePayload | null;
  draft: SourceDraft;
  setDraft: (draft: SourceDraft) => void;
  onSave: () => void;
  onTest: () => void;
  onRefreshUsage: () => void;
  onToggleSource: (source: string, enabled: boolean) => void;
  busy: boolean;
  usageRefreshing: boolean;
}) {
  const { usage, draft, setDraft, onSave, onTest, onRefreshUsage, onToggleSource, busy, usageRefreshing } = props;
  const rows = Array.isArray(usage?.sources) ? usage.sources : [];
  const totals = usage?.totals || {};
  return (
    <div className="manage-source-console">
      <div className="manage-card manage-provider-ops-card manage-card-deep">
        <div className="manage-panel-head">
          <div>
            <p className="manage-panel-kicker">Source Ops</p>
            <h3>文献源状态与召回记录</h3>
          </div>
          <div className="manage-actions">
            <span className="manage-panel-meta">
              {formatNumber(totals.schedulable)} / {formatNumber(totals.sources)} schedulable
            </span>
            <button className="manage-secondary" type="button" onClick={onRefreshUsage} disabled={busy || usageRefreshing}>
              {usageRefreshing ? '刷新中' : '刷新状态'}
            </button>
          </div>
        </div>
        <div className="manage-provider-ops-summary manage-source-ops-summary">
          <div><span>24h Calls</span><strong>{formatNumber(totals.calls24h)}</strong><small>{formatNumber(totals.resultCount24h)} results</small></div>
          <div><span>7d Calls</span><strong>{formatNumber(totals.calls7d)}</strong><small>{formatNumber(totals.resultCount7d)} results</small></div>
          <div><span>Total Calls</span><strong>{formatNumber(totals.calls)}</strong><small>{formatNumber(totals.resultCount)} results</small></div>
          <div><span>Rate Limited</span><strong>{formatNumber(totals.rateLimited24h)}</strong><small>7d {formatNumber(totals.rateLimited7d)}</small></div>
        </div>
        <div className="manage-source-ops-grid">
          {rows.length > 0 ? rows.map((row) => {
            const day = sourceWindow(row, 'last24h');
            const week = sourceWindow(row, 'last7d');
            const total = sourceWindow(row, 'total');
            const status = sourceStatus(row);
            return (
              <article className="manage-provider-ops-row manage-source-ops-row" key={row.source}>
                <div className="manage-provider-ops-row-head">
                  <div>
                    <span className={`manage-status ${sourceRowTone(row)}`}>{status}</span>
                    <strong>{displayText(row.source)}</strong>
                    <small>{displayText(row.baseUrl, 'default baseUrl')} · {formatDecimal(row.rps || 0)} rps / {formatNumber(row.concurrency || 0)}c</small>
                  </div>
                  <button
                    className={row.enabled === false ? 'manage-primary' : 'manage-secondary'}
                    type="button"
                    onClick={() => onToggleSource(row.source, row.enabled === false)}
                    disabled={busy || status === 'orphaned'}
                  >
                    {row.enabled === false ? '启用' : '停用'}
                  </button>
                </div>
                <div className="manage-provider-ops-metrics">
                  <span>24h {formatNumber(day.calls)} calls</span>
                  <span>{formatNumber(day.resultCount)} results</span>
                  <span>{formatPercent(sourceErrorRate(day))} err/rl</span>
                  <span>{formatMs(day.avgLatencyMs)}</span>
                </div>
                <div className="manage-provider-ops-metrics">
                  <span>7d {formatNumber(week.calls)}</span>
                  <span>total {formatNumber(total.calls)}</span>
                  <span>rate {formatNumber(total.rateLimitedCalls)}</span>
                  <span>key {row.keyCount ? `${formatNumber(row.keyCount)} refs` : 'none'}</span>
                </div>
                <div className="manage-provider-ops-metrics">
                  <span>secret {row.hasConfiguredKey ? 'ok' : row.keyCount ? 'missing' : 'optional'}</span>
                  <span>email {row.email ? 'ok' : row.requiresEmail ? 'missing' : 'optional'}</span>
                  <span>{row.tool || 'no tool'}</span>
                  <span>{row.rpsPerKey ? `${formatDecimal(row.rpsPerKey)} rps/key` : 'pool rps'}</span>
                </div>
                <small className="manage-provider-ops-footnote">
                  last {row.lastCall ? `${String(row.lastCall.status || '-')} · ${formatIsoTime(row.lastCall.at)} · ${formatMs(row.lastCall.latencyMs)} · ${formatNumber(row.lastCall.resultCount)} results` : 'no call'}
                  {row.lastCall?.error ? ` · ${String(row.lastCall.error).slice(0, 120)}` : ''}
                  {' · '}{row.reason || 'ready'}
                </small>
              </article>
            );
          }) : <p className="manage-muted">暂无 source 用量。执行文献源探测或创建任务后会出现。</p>}
        </div>
        <p className="manage-muted">
          Source 配置存 D1 system_config.source_factory；真实 key 只通过 Worker secret / .dev.vars 引用；调用记录存 D1 source_calls；检索快照和完整 payload 进入 D1 artifact 分片，D1 同时保留轻索引和审计账本。
        </p>
      </div>

      <div className="manage-provider-editor manage-source-editor">
        <div className="manage-panel-head">
          <div>
            <p className="manage-panel-kicker">Source Factory</p>
            <h3>调整文献源配置</h3>
          </div>
          <span className="manage-panel-meta">只保存 key 引用，不保存明文 key</span>
        </div>
        <div className="manage-provider-form-grid">
          <label><span>Source</span><select className="manage-input" value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })}>
            {['openalex', 'crossref', 'semantic_scholar', 'pubmed', 'arxiv', 'biorxiv', 'medrxiv'].map((source) => <option key={source} value={source}>{source}</option>)}
          </select></label>
          <label><span>Base URL</span><input className="manage-input" value={draft.baseUrl} onChange={(e) => setDraft({ ...draft, baseUrl: e.target.value })} placeholder="留空使用默认官方接口" /></label>
          <label><span>API Key refs</span><input className="manage-input" value={draft.apiKeyRefs} onChange={(e) => setDraft({ ...draft, apiKeyRefs: e.target.value })} placeholder="SEMANTIC_SCHOLAR_API_KEY,OPENALEX_API_KEY" /></label>
          <label><span>Email / mailto</span><input className="manage-input" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="PubMed/OpenAlex/Crossref 推荐配置" /></label>
          <label><span>Tool</span><input className="manage-input" value={draft.tool} onChange={(e) => setDraft({ ...draft, tool: e.target.value })} placeholder="NCBI tool name" /></label>
          <label><span>RPS</span><input className="manage-input" value={draft.rps} onChange={(e) => setDraft({ ...draft, rps: e.target.value })} inputMode="decimal" /></label>
          <label><span>RPS / Key</span><input className="manage-input" value={draft.rpsPerKey} onChange={(e) => setDraft({ ...draft, rpsPerKey: e.target.value })} placeholder="留空按总 RPS" inputMode="decimal" /></label>
          <label><span>并发</span><input className="manage-input" value={draft.concurrency} onChange={(e) => setDraft({ ...draft, concurrency: e.target.value })} inputMode="numeric" /></label>
          <label className="manage-provider-check"><input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> 启用</label>
        </div>
        <div className="manage-actions">
          <button className="manage-primary" onClick={onSave} disabled={busy || !draft.source.trim()}>保存 source</button>
          <button className="manage-secondary" onClick={onTest} disabled={busy || !draft.source.trim()}>探测 source</button>
        </div>
      </div>
    </div>
  );
}

export default function ManagePanel({ initialKey = '' }: ManagePanelProps) {
  const navigate = useNavigate();
  const [manageKey, setManageKey] = useState(() => sessionStorage.getItem('manageKey') || initialKey);
  const [manageAccess, setManageAccess] = useState(() => Boolean(sessionStorage.getItem('manageKey') || sessionStorage.getItem('manageRoleSession') === 'true' || initialKey));
  const [keyInput, setKeyInput] = useState(() => sessionStorage.getItem('manageKey') || initialKey);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [bootMessage, setBootMessage] = useState('');
  const [loginMode, setLoginMode] = useState<'admin' | 'user'>('user');
  const [error, setError] = useState('');
  const [tab, setTab] = useState<TabKey>('overview');
  const [summary, setSummary] = useState<ManageSummary | null>(null);
  const [databaseAudit, setDatabaseAudit] = useState<ManageDatabaseAudit | null>(null);
  const [freePlanUsage, setFreePlanUsage] = useState<ManageFreePlanUsage | null>(null);
  const [billingOps, setBillingOps] = useState<ManageBillingOps | null>(null);
  const [deploymentPlan, setDeploymentPlan] = useState<DeploymentPlan | null>(null);
  const [summaryError, setSummaryError] = useState('');
  const [databaseError, setDatabaseError] = useState('');
  const [billingError, setBillingError] = useState('');
  const [servicesError, setServicesError] = useState('');
  const [deploymentPlanError, setDeploymentPlanError] = useState('');
  const [runs, setRuns] = useState<ManageRun[]>([]);
  const [runsError, setRunsError] = useState('');
  const [runsHasMore, setRunsHasMore] = useState(false);
  const [runsOffset, setRunsOffset] = useState(0);
  const [users, setUsers] = useState<ManageUser[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersError, setUsersError] = useState('');
  const [usersRefreshing, setUsersRefreshing] = useState(false);
  const [userQuery, setUserQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState<Record<string, any> | null>(null);
  const [selectedUserError, setSelectedUserError] = useState('');
  const [creditAdjustment, setCreditAdjustment] = useState({ amountCredits: '100', reason: 'manual adjustment' });
  const [selectedRunId, setSelectedRunId] = useState('');
  const [runDetail, setRunDetail] = useState<Record<string, any> | null>(null);
  const [detailError, setDetailError] = useState('');
  const [config, setConfig] = useState<Record<string, any> | null>(null);
  const [configError, setConfigError] = useState('');
  const [configPatch, setConfigPatch] = useState('');
  const [providerUsage, setProviderUsage] = useState<ManageProviderUsagePayload | null>(null);
  const [providerUsageError, setProviderUsageError] = useState('');
  const [sourceUsage, setSourceUsage] = useState<ManageSourceUsagePayload | null>(null);
  const [sourceUsageError, setSourceUsageError] = useState('');
  const [providerDraft, setProviderDraft] = useState<ProviderDraft>(EMPTY_PROVIDER_DRAFT);
  const [sourceDraft, setSourceDraft] = useState<SourceDraft>(EMPTY_SOURCE_DRAFT);
  const [testEmailDraft, setTestEmailDraft] = useState<TestEmailDraft>(EMPTY_TEST_EMAIL_DRAFT);
  const [testEmailResult, setTestEmailResult] = useState<Record<string, any> | null>(null);
  const [testTarget, setTestTarget] = useState('llm');
  const [testName, setTestName] = useState('search_specialist');
  const [testConfig, setTestConfig] = useState('');
  const [testResult, setTestResult] = useState<Record<string, any> | null>(null);
  const [providerTestResult, setProviderTestResult] = useState<Record<string, any> | null>(null);
  const [providerTestError, setProviderTestError] = useState('');
  const [testingProviderId, setTestingProviderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [summaryRefreshing, setSummaryRefreshing] = useState(false);
  const [databaseRefreshing, setDatabaseRefreshing] = useState(false);
  const [servicesRefreshing, setServicesRefreshing] = useState(false);
  const [runsRefreshing, setRunsRefreshing] = useState(false);
  const [configRefreshing, setConfigRefreshing] = useState(false);
  const [providerUsageRefreshing, setProviderUsageRefreshing] = useState(false);
  const [sourceUsageRefreshing, setSourceUsageRefreshing] = useState(false);
  const [billingRefreshing, setBillingRefreshing] = useState(false);
  const [detailRefreshing, setDetailRefreshing] = useState(false);
  const [autoRefreshMs, setAutoRefreshMs] = useState<number>(10000);
  const [pressureHistory, setPressureHistory] = useState<PressurePoint[]>([]);
  const refreshCycleRef = useRef(false);
  const selectedRunIdRef = useRef(selectedRunId);
  const runDetailRequestIdRef = useRef(0);
  const runsRef = useRef<ManageRun[]>([]);
  const runsOffsetRef = useRef(0);

  const authenticated = manageAccess;
  const supabaseConfigured = hasSupabaseConfig();
  const refreshing = summaryRefreshing || databaseRefreshing || servicesRefreshing || runsRefreshing || usersRefreshing || configRefreshing || providerUsageRefreshing || sourceUsageRefreshing || billingRefreshing || detailRefreshing;

  useEffect(() => {
    selectedRunIdRef.current = selectedRunId;
  }, [selectedRunId]);

  useEffect(() => {
    runsRef.current = runs;
  }, [runs]);

  useEffect(() => {
    runsOffsetRef.current = runsOffset;
  }, [runsOffset]);

  const appendPressurePoint = useCallback((nextPressure: Record<string, any> | undefined) => {
    if (!nextPressure) return;
    const cpu = clampPercent(nextPressure.cpuPercent);
    const memory = clampPercent(nextPressure.memoryPercent);
    const disk = clampPercent(nextPressure.diskPercent);
    if (![cpu, memory, disk].every((value) => Number.isFinite(value))) return;
    setPressureHistory((previous) => [...previous, { at: Date.now(), cpu, memory, disk }].slice(-24));
  }, []);

  const loadSummary = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (!options.silent) setSummaryRefreshing(true);
    try {
      const nextSummary = await getManageSummary(key);
      setSummary((previous) => {
        const merged = {
          ...(previous || {}),
          ...(nextSummary as ManageSummary),
          services: previous?.services || (nextSummary as ManageSummary).services,
        } as ManageSummary;
        return merged;
      });
      appendPressurePoint((nextSummary as ManageSummary).pressure);
      setSummaryError('');
      return nextSummary as ManageSummary;
    } catch (err) {
      setSummaryError(getErrorMessage(err, '读取总览失败'));
      return null;
    } finally {
      if (!options.silent) setSummaryRefreshing(false);
    }
  }, [appendPressurePoint, manageKey]);

  const loadDatabaseAudit = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (!options.silent) setDatabaseRefreshing(true);
    try {
      const [payload, freePlanPayload] = await Promise.all([
        getManageDatabase(key),
        getManageFreePlanUsage(key).catch(() => null),
      ]);
      const auditPayload = await getManageAuditLogs(key, 20).catch(() => ({ auditLogs: [] }));
      const nextFreePlan = ((freePlanPayload as ManageFreePlanUsage | null) || (payload.freePlanUsage as ManageFreePlanUsage | undefined) || null);
      setFreePlanUsage(nextFreePlan);
      setDatabaseAudit({
        ...(payload as ManageDatabaseAudit),
        freePlanUsage: nextFreePlan,
        auditLogs: Array.isArray(auditPayload.auditLogs) ? auditPayload.auditLogs : [],
      });
      setDatabaseError('');
      return payload as ManageDatabaseAudit;
    } catch (err) {
      setDatabaseError(getErrorMessage(err, '读取数据库审计失败'));
      return null;
    } finally {
      if (!options.silent) setDatabaseRefreshing(false);
    }
  }, [manageKey]);

  const loadServices = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (!options.silent) setServicesRefreshing(true);
    try {
      const [payload, planPayload] = await Promise.all([
        getManageServices(key),
        getManageDeploymentPlan(key).catch(() => null),
      ]);
      const nextServices = Array.isArray(payload.services) ? (payload.services as ManageService[]) : [];
      const nextPlan = ((payload.deploymentPlan as DeploymentPlan | undefined) || planPayload || null) as DeploymentPlan | null;
      setDeploymentPlan(nextPlan);
      setSummary((previous) => ({
        ...(previous || {}),
        updatedAt: typeof payload.updatedAt === 'string' ? payload.updatedAt : previous?.updatedAt,
        services: nextServices,
      } as ManageSummary));
      setServicesError('');
      setDeploymentPlanError('');
      return nextServices;
    } catch (err) {
      setServicesError(getErrorMessage(err, '读取服务健康失败'));
      setDeploymentPlanError(getErrorMessage(err, '读取部署计划失败'));
      return [] as ManageService[];
    } finally {
      if (!options.silent) setServicesRefreshing(false);
    }
  }, [manageKey]);

  const loadRuns = useCallback(async (key = manageKey, options: LoadOptions & { append?: boolean; offset?: number; limit?: number } = {}) => {
    if (!options.silent) setRunsRefreshing(true);
    const offset = Math.max(0, options.offset ?? 0);
    const limit = Math.max(1, options.limit ?? 20);
    try {
      const payload = await getManageRuns(key, limit, offset);
      const nextRuns = Array.isArray(payload.runs) ? (payload.runs as ManageRun[]) : [];
      setRuns((current) => {
        const merged = options.append ? [...current, ...nextRuns] : nextRuns;
        const deduped = new Map<string, ManageRun>();
        for (const run of merged) {
          const id = runIdentity(run);
          if (id) deduped.set(id, run);
        }
        return Array.from(deduped.values());
      });
      setRunsHasMore(Boolean(payload.hasMore));
      setRunsOffset(offset + nextRuns.length);
      setRunsError('');
      setSelectedRunId((current) => {
        const mergedRuns = options.append ? [...runsRef.current, ...nextRuns] : nextRuns;
        if (mergedRuns.some((run) => runIdentity(run) === current)) return current;
        return runIdentity(mergedRuns[0]) || '';
      });
      return nextRuns;
    } catch (err) {
      setRunsError(getErrorMessage(err, '读取任务历史失败'));
      return [] as ManageRun[];
    } finally {
      if (!options.silent) setRunsRefreshing(false);
    }
  }, [manageKey]);

  const loadUsers = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (!options.silent) setUsersRefreshing(true);
    try {
      const payload = await getManageUsers(key, {
        limit: 50,
        offset: 0,
        q: userQuery.trim(),
        role: userRoleFilter,
        status: userStatusFilter,
      });
      const nextUsers = Array.isArray(payload.users) ? (payload.users as ManageUser[]) : [];
      setUsers(nextUsers);
      setUsersTotal(Number(payload.total || nextUsers.length || 0));
      setUsersError('');
      setSelectedUserId((current) => {
        if (nextUsers.some((user) => user.id === current)) return current;
        return String(nextUsers[0]?.id || '');
      });
      return nextUsers;
    } catch (err) {
      setUsersError(getErrorMessage(err, '读取用户列表失败'));
      return [] as ManageUser[];
    } finally {
      if (!options.silent) setUsersRefreshing(false);
    }
  }, [manageKey, userQuery, userRoleFilter, userStatusFilter]);

  const loadBillingOps = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (!options.silent) setBillingRefreshing(true);
    try {
      const [summaryPayload, paymentsPayload, ledgerPayload, deliveriesPayload] = await Promise.all([
        getManageBillingSummary(key),
        getManagePayments(key, { limit: 50 }),
        getManageLedger(key, { limit: 50 }),
        getManageEmailDeliveries(key, { limit: 50 }),
      ]);
      const next: ManageBillingOps = {
        updatedAt: typeof summaryPayload.updatedAt === 'string' ? summaryPayload.updatedAt : new Date().toISOString(),
        summary: (summaryPayload.summary as Record<string, any>) || {},
        payments: Array.isArray(paymentsPayload.payments) ? (paymentsPayload.payments as ManagePayment[]) : [],
        paymentsTotal: Number(paymentsPayload.total || 0),
        ledger: Array.isArray(ledgerPayload.ledger) ? (ledgerPayload.ledger as ManageLedgerRow[]) : [],
        ledgerTotal: Number(ledgerPayload.total || 0),
        deliveries: Array.isArray(deliveriesPayload.deliveries) ? (deliveriesPayload.deliveries as ManageEmailDelivery[]) : [],
        deliveriesTotal: Number(deliveriesPayload.total || 0),
      };
      setBillingOps(next);
      setBillingError('');
      return next;
    } catch (err) {
      setBillingError(getErrorMessage(err, '读取账务与邮件运营失败'));
      return null;
    } finally {
      if (!options.silent) setBillingRefreshing(false);
    }
  }, [manageKey]);

  const loadSelectedUser = useCallback(async (userId: string, key = manageKey, options: LoadOptions = {}) => {
    if (!userId) {
      setSelectedUser(null);
      setSelectedUserError('');
      return null;
    }
    if (!options.silent) setUsersRefreshing(true);
    try {
      const payload = await getManageUser(key, userId);
      setSelectedUser(payload as Record<string, any>);
      setSelectedUserError('');
      return payload;
    } catch (err) {
      setSelectedUserError(getErrorMessage(err, '读取用户详情失败'));
      return null;
    } finally {
      if (!options.silent) setUsersRefreshing(false);
    }
  }, [manageKey]);

  const loadConfig = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (!options.silent) setConfigRefreshing(true);
    try {
      const payload = await getManageConfig(key);
      setConfig((payload.config as Record<string, any>) || {});
      setConfigError('');
      return payload;
    } catch (err) {
      setConfigError(getErrorMessage(err, '读取配置失败'));
      return null;
    } finally {
      if (!options.silent) setConfigRefreshing(false);
    }
  }, [manageKey]);

  const loadProviderUsage = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (!options.silent) setProviderUsageRefreshing(true);
    try {
      const payload = await getManageProviderUsage(key);
      setProviderUsage(payload as ManageProviderUsagePayload);
      setProviderUsageError('');
      return payload as ManageProviderUsagePayload;
    } catch (err) {
      setProviderUsageError(getErrorMessage(err, '读取 provider 用量失败'));
      return null;
    } finally {
      if (!options.silent) setProviderUsageRefreshing(false);
    }
  }, [manageKey]);

  const loadSourceUsage = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (!options.silent) setSourceUsageRefreshing(true);
    try {
      const payload = await getManageSourceUsage(key);
      setSourceUsage(payload as ManageSourceUsagePayload);
      setSourceUsageError('');
      return payload as ManageSourceUsagePayload;
    } catch (err) {
      setSourceUsageError(getErrorMessage(err, '读取 source 用量失败'));
      return null;
    } finally {
      if (!options.silent) setSourceUsageRefreshing(false);
    }
  }, [manageKey]);

  const loadRunDetail = useCallback(async (runId: string, key = manageKey, options: LoadOptions = {}) => {
    if (!runId) {
      setRunDetail(null);
      setDetailError('');
      return null;
    }
    const requestId = runDetailRequestIdRef.current + 1;
    runDetailRequestIdRef.current = requestId;
    if (!options.silent) setDetailRefreshing(true);
    if (!options.preserve) setRunDetail(null);
    try {
      let detail = await getManageRunDetail(key, runId);
      if (!Array.isArray((detail as any).computeJobs) && !Array.isArray((detail as any).compute_jobs)) {
        const jobsPayload = await getRunComputeJobs(runId).catch(() => null);
        const jobs = Array.isArray(jobsPayload?.jobs)
          ? jobsPayload.jobs
          : Array.isArray((jobsPayload as any)?.computeJobs)
            ? (jobsPayload as any).computeJobs
            : [];
        if (jobs.length > 0) detail = { ...detail, computeJobs: jobs };
      }
      if (!(detail as any).computePerformance && !(detail as any).compute_performance) {
        const perfPayload = await getRunComputePerformance(runId, 12).catch(() => null);
        if (perfPayload && typeof perfPayload === 'object') {
          detail = { ...detail, computePerformance: perfPayload };
        }
      }
      if (runDetailRequestIdRef.current !== requestId || selectedRunIdRef.current !== runId) return null;
      setRunDetail(detail);
      setDetailError('');
      return detail;
    } catch (err) {
      if (runDetailRequestIdRef.current === requestId && selectedRunIdRef.current === runId) {
        setDetailError(getErrorMessage(err, '读取任务详情失败'));
        if (!options.preserve) setRunDetail(null);
      }
      return null;
    } finally {
      if (runDetailRequestIdRef.current === requestId && !options.silent) {
        setDetailRefreshing(false);
      }
    }
  }, [manageKey]);

  const refreshAll = useCallback(async (key = manageKey, options: LoadOptions = {}) => {
    if (refreshCycleRef.current) return;
    refreshCycleRef.current = true;
    try {
      const activeRunId = selectedRunIdRef.current;
      await Promise.allSettled([
        loadSummary(key, options),
        loadRuns(key, { ...options, limit: Math.max(runsOffsetRef.current || 20, 20) }),
        activeRunId ? loadRunDetail(activeRunId, key, { ...options, preserve: true }) : Promise.resolve(null),
        tab === 'billing' ? loadBillingOps(key, options) : Promise.resolve(null),
        tab === 'config' ? loadProviderUsage(key, { ...options, silent: true }) : Promise.resolve(null),
        tab === 'config' ? loadSourceUsage(key, { ...options, silent: true }) : Promise.resolve(null),
      ]);
    } finally {
      refreshCycleRef.current = false;
    }
  }, [loadBillingOps, loadProviderUsage, loadRunDetail, loadRuns, loadSourceUsage, loadSummary, manageKey, tab]);

  useEffect(() => {
    const boot = async () => {
      try {
        const data = await getManageBootstrap();
        if (data?.created && data?.key) {
          setBootMessage(`首次启动已生成管理 key：${data.key}。请立即保存。`);
          setKeyInput(String(data.key));
        } else {
          setBootMessage('');
        }
      } catch {
        setBootMessage('管理后台暂时无法连接后端。');
      }
    };
    void boot();
  }, []);

  useEffect(() => {
    if (!authenticated) return;
    void Promise.allSettled([
      loadSummary(manageKey),
      loadRuns(manageKey, { limit: 20 }),
      loadUsers(manageKey, { silent: true }),
      loadBillingOps(manageKey, { silent: true }),
    ]);
  }, [authenticated, loadBillingOps, loadRuns, loadSummary, loadUsers, manageKey]);

  useEffect(() => {
    if (!authenticated || tab !== 'users') return;
    void loadUsers(manageKey);
  }, [authenticated, loadUsers, manageKey, tab]);

  useEffect(() => {
    if (!authenticated || tab !== 'billing') return;
    void loadBillingOps(manageKey);
  }, [authenticated, loadBillingOps, manageKey, tab]);

  useEffect(() => {
    if (!authenticated || tab !== 'config') return;
    void Promise.allSettled([
      loadConfig(manageKey),
      loadProviderUsage(manageKey),
      loadSourceUsage(manageKey),
    ]);
  }, [authenticated, loadConfig, loadProviderUsage, loadSourceUsage, manageKey, tab]);

  useEffect(() => {
    if (!authenticated || !selectedUserId) return;
    void loadSelectedUser(selectedUserId, manageKey, { silent: true });
  }, [authenticated, loadSelectedUser, manageKey, selectedUserId]);

  useEffect(() => {
    if (!authenticated) return;
    void loadDatabaseAudit(manageKey, { silent: true });
  }, [authenticated, loadDatabaseAudit, manageKey]);

  useEffect(() => {
    if (!authenticated) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const loop = async () => {
      if (!cancelled && document.visibilityState !== 'hidden') {
        await refreshAll(manageKey, { silent: true, preserve: true });
      }
      if (!cancelled) {
        timer = window.setTimeout(loop, autoRefreshMs);
      }
    };

    timer = window.setTimeout(loop, autoRefreshMs);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [authenticated, autoRefreshMs, manageKey, refreshAll]);

  useEffect(() => {
    if (!authenticated) return;
    if (!selectedRunId) {
      setRunDetail(null);
      setDetailError('');
      return;
    }
    void loadRunDetail(selectedRunId, manageKey, { preserve: false });
  }, [authenticated, loadRunDetail, manageKey, selectedRunId]);

  const handleLogin = async () => {
    setBusy(true);
    setError('');
    try {
      const key = keyInput.trim();
      await loginManage(key);
      sessionStorage.setItem('manageKey', key);
      sessionStorage.removeItem('manageRoleSession');
      setManageKey(key);
      setManageAccess(true);
      await Promise.allSettled([
        loadSummary(key),
        loadRuns(key, { limit: 20 }),
        loadUsers(key, { silent: true }),
        loadBillingOps(key, { silent: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, '登录失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleUserSignIn = async () => {
    setBusy(true);
    setError('');
    try {
      const client = await getSupabaseClient();
      if (!client) throw new Error('Supabase URL / anon key 未配置');
      const { data, error: signInError } = await client.auth.signInWithPassword({
        email: userEmail.trim(),
        password: userPassword,
      });
      if (signInError) throw signInError;
      if (!data.session?.access_token) throw new Error('Supabase 未返回 access token');
      notifyAuthChange();
      await updateEmailSettings({ resultEmail: userEmail.trim(), sendResultEmail: true, defaultArtifactTtlHours: 24 });
      navigate('/account');
    } catch (err) {
      setError(getErrorMessage(err, '用户登录失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleUserSignUp = async () => {
    setBusy(true);
    setError('');
    try {
      const client = await getSupabaseClient();
      if (!client) throw new Error('Supabase URL / anon key 未配置');
      const { data, error: signUpError } = await client.auth.signUp({
        email: userEmail.trim(),
        password: userPassword,
      });
      if (signUpError) throw signUpError;
      if (data.session?.access_token) {
        notifyAuthChange();
        await updateEmailSettings({ resultEmail: userEmail.trim(), sendResultEmail: true, defaultArtifactTtlHours: 24 });
        navigate('/account');
        return;
      }
    } catch (err) {
      setError(getErrorMessage(err, '用户注册失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleSelectRun = (runId: string) => {
    setSelectedRunId(runId);
    setDetailError('');
  };

  const handleLoadMoreRuns = async () => {
    if (!runsHasMore || runsRefreshing) return;
    await loadRuns(manageKey, { append: true, offset: runsOffset, limit: 20 });
  };

  const handleUpdateUser = async (userId: string, patch: Record<string, unknown>) => {
    if (!userId) return;
    setBusy(true);
    setError('');
    try {
      await updateManageUser(manageKey, userId, patch);
      await Promise.allSettled([
        loadUsers(manageKey, { silent: true }),
        loadSelectedUser(userId, manageKey, { silent: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, '用户更新失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleAdjustUserCredits = async () => {
    if (!selectedUserId) return;
    const amountCredits = Math.trunc(Number(creditAdjustment.amountCredits));
    if (!Number.isFinite(amountCredits) || amountCredits === 0) {
      setError('额度调整必须是非零整数');
      return;
    }
    const reason = creditAdjustment.reason.trim();
    if (!reason) {
      setError('请填写额度调整原因');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await adjustManageUserCredits(manageKey, selectedUserId, {
        amountCredits,
        reason,
        idempotencyKey: `ui-${selectedUserId}-${Date.now()}`,
      });
      await Promise.allSettled([
        loadUsers(manageKey, { silent: true }),
        loadSelectedUser(selectedUserId, manageKey, { silent: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, '额度调整失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleRunAction = async (action: 'cancel' | 'resume') => {
    if (!selectedRunId) return;
    setBusy(true);
    setError('');
    try {
      await manageActionRun(manageKey, selectedRunId, action);
      await Promise.allSettled([
        loadSummary(manageKey),
        loadRuns(manageKey, { limit: Math.max(runsOffsetRef.current || 20, 20) }),
        loadRunDetail(selectedRunId, manageKey, { preserve: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, '任务操作失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleRefundPayment = async (payment: ManagePayment) => {
    const sessionId = String(payment.provider_session_id || '').trim();
    const amountCents = Math.trunc(Number(payment.amount_cents || 0));
    if (!sessionId || amountCents <= 0 || !canRefundStripePayment(payment)) {
      setBillingError('该支付记录缺少 Stripe session/payment_intent，或当前状态不允许退款');
      return;
    }
    const confirmed = typeof window === 'undefined'
      ? true
      : window.confirm('确认向 Stripe 发起退款？额度扣减会等待 Stripe refund webhook 回写，后台不会立即改 credit ledger。');
    if (!confirmed) return;

    setBusy(true);
    setBillingError('');
    try {
      await refundManagePayment(manageKey, sessionId, {
        amountCents,
        reason: 'requested_by_customer',
        idempotencyKey: `ui-refund-${sessionId}-${amountCents}`,
      });
      await Promise.allSettled([
        loadBillingOps(manageKey),
        loadSummary(manageKey, { silent: true }),
      ]);
    } catch (err) {
      setBillingError(getErrorMessage(err, 'Stripe 退款发起失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleSendTestEmail = async () => {
    const toEmail = testEmailDraft.toEmail.trim();
    if (!toEmail || !toEmail.includes('@')) {
      setBillingError('请填写有效的测试邮件收件人');
      return;
    }
    setBusy(true);
    setBillingError('');
    setTestEmailResult(null);
    try {
      const result = await sendManageTestEmail(manageKey, {
        toEmail,
        subject: testEmailDraft.subject.trim() || 'Academic Cluster Resend smoke',
        text: testEmailDraft.text.trim() || EMPTY_TEST_EMAIL_DRAFT.text,
        idempotencyKey: `ui-resend-test-${toEmail}-${Date.now()}`,
      });
      setTestEmailResult(result as Record<string, any>);
      await Promise.allSettled([
        loadBillingOps(manageKey),
        loadSummary(manageKey, { silent: true }),
      ]);
    } catch (err) {
      setBillingError(getErrorMessage(err, 'Resend 测试邮件发送失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveConfig = async () => {
    setBusy(true);
    setError('');
    try {
      const patch = JSON.parse(configPatch || '{}');
      const res = await updateManageConfig(manageKey, patch);
      setConfig((res.config as Record<string, any>) || {});
      setConfigPatch('');
      setConfigError('');
      await Promise.allSettled([
        loadConfig(manageKey, { silent: true }),
        loadSummary(manageKey, { silent: true }),
        loadProviderUsage(manageKey, { silent: true }),
        loadSourceUsage(manageKey, { silent: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, '配置保存失败，请检查 JSON'));
    } finally {
      setBusy(false);
    }
  };

  const handleSaveProvider = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await updateManageConfig(manageKey, providerDraftToPatch(providerDraft));
      setConfig((res.config as Record<string, any>) || {});
      setProviderDraft({ ...EMPTY_PROVIDER_DRAFT, kind: providerDraft.kind, scope: providerDraft.scope });
      await Promise.allSettled([
        loadConfig(manageKey, { silent: true }),
        loadSummary(manageKey, { silent: true }),
        loadProviderUsage(manageKey, { silent: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Provider 保存失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleTestProvider = async () => {
    setBusy(true);
    setError('');
    setProviderTestError('');
    setProviderTestResult(null);
    setTestResult(null);
    try {
      const target = providerDraft.kind;
      setTestTarget(target);
      setTestName(providerDraft.kind === 'llm' ? providerDraft.scope || 'global' : target);
      const result = await testManageTarget(manageKey, target, providerDraft.scope || undefined, providerDraftToConfig(providerDraft));
      setProviderTestResult(result);
      setTestResult(result);
    } catch (err) {
      const message = getErrorMessage(err, 'Provider 测试失败');
      setProviderTestError(message);
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleTestConfiguredProvider = async (row: ProviderUsageRow) => {
    const target = String(row.taskType || '') as ProviderKind;
    if (!(['llm', 'embedding', 'rerank'] as string[]).includes(target) || !row.id) return;
    setBusy(true);
    setTestingProviderId(row.id);
    setError('');
    setProviderTestError('');
    setProviderTestResult(null);
    setTestResult(null);
    try {
      const result = await testManageTarget(manageKey, target, row.id, { providerId: row.id });
      setProviderTestResult(result);
      setTestResult(result);
      await Promise.allSettled([
        loadProviderUsage(manageKey, { silent: true }),
        loadConfig(manageKey, { silent: true }),
      ]);
    } catch (err) {
      const message = getErrorMessage(err, 'Provider 测试失败');
      setProviderTestError(`${row.id}: ${message}`);
      setError(message);
    } finally {
      setTestingProviderId('');
      setBusy(false);
    }
  };

  const handleToggleProvider = async (providerId: string, enabled: boolean) => {
    if (!providerId) return;
    setBusy(true);
    setError('');
    try {
      const payload = await updateManageProvider(manageKey, providerId, { enabled });
      setProviderUsage(payload as ManageProviderUsagePayload);
      await Promise.allSettled([
        loadConfig(manageKey, { silent: true }),
        loadSummary(manageKey, { silent: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Provider 状态更新失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleClearProviderCooldown = async (providerId: string) => {
    if (!providerId) return;
    setBusy(true);
    setError('');
    try {
      const payload = await updateManageProvider(manageKey, providerId, { clearTempUnschedulable: true });
      setProviderUsage(payload as ManageProviderUsagePayload);
      await loadConfig(manageKey, { silent: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Provider 冷却清除失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteProvider = async (providerId: string) => {
    if (!providerId) {
      setError('Provider 删除失败：缺少 provider id');
      return;
    }
    setProviderTestError('');
    const confirmed = typeof window === 'undefined'
      ? true
      : window.confirm(`删除 provider ${providerId}？这会同时清理它的调用历史，避免删除后又作为历史记录占位。`);
    if (!confirmed) return;
    setBusy(true);
    setError('');
    try {
      const payload = await deleteManageProvider(manageKey, providerId);
      setProviderUsage(payload as ManageProviderUsagePayload);
      setProviderTestResult({ ok: true, deletedProviderId: providerId });
      await Promise.allSettled([
        loadConfig(manageKey, { silent: true }),
        loadSummary(manageKey, { silent: true }),
        loadProviderUsage(manageKey, { silent: true }),
      ]);
    } catch (err) {
      const message = getErrorMessage(err, 'Provider 删除失败');
      setProviderTestError(message);
      setError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveSource = async () => {
    setBusy(true);
    setError('');
    try {
      const patch: Record<string, unknown> = {
        enabled: sourceDraft.enabled,
        rps: Number(sourceDraft.rps || 1),
        concurrency: Number(sourceDraft.concurrency || 1),
      };
      if (sourceDraft.rpsPerKey.trim()) patch.rpsPerKey = Number(sourceDraft.rpsPerKey);
      patch.apiKeyRefs = sourceDraft.apiKeyRefs
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      if (sourceDraft.email.trim()) patch.email = sourceDraft.email.trim();
      else patch.email = '';
      if (sourceDraft.tool.trim()) patch.tool = sourceDraft.tool.trim();
      else patch.tool = '';
      if (sourceDraft.baseUrl.trim()) patch.baseUrl = sourceDraft.baseUrl.trim();
      else patch.baseUrl = '';
      const payload = await updateManageSource(manageKey, sourceDraft.source, patch);
      setSourceUsage(payload as ManageSourceUsagePayload);
      await Promise.allSettled([
        loadConfig(manageKey, { silent: true }),
        loadSummary(manageKey, { silent: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Source 保存失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleTestSource = async () => {
    setBusy(true);
    setError('');
    setTestResult(null);
    try {
      setTestTarget(sourceDraft.source);
      setTestName('academic review');
      setTestResult(await testManageTarget(manageKey, sourceDraft.source, 'academic review', {
        query: 'academic review',
        maxResults: 5,
      }));
      await loadSourceUsage(manageKey, { silent: true });
    } catch (err) {
      setError(getErrorMessage(err, 'Source 探测失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleSource = async (source: string, enabled: boolean) => {
    if (!source) return;
    setBusy(true);
    setError('');
    try {
      const payload = await updateManageSource(manageKey, source, { enabled });
      setSourceUsage(payload as ManageSourceUsagePayload);
      await Promise.allSettled([
        loadConfig(manageKey, { silent: true }),
        loadSummary(manageKey, { silent: true }),
      ]);
    } catch (err) {
      setError(getErrorMessage(err, 'Source 状态更新失败'));
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    setTestResult(null);
    setError('');
    try {
      const parsed = testConfig.trim() ? JSON.parse(testConfig) : undefined;
      setTestResult(await testManageTarget(manageKey, testTarget, testName || undefined, parsed));
    } catch (err) {
      setError(getErrorMessage(err, '测试失败，请检查 JSON'));
    } finally {
      setBusy(false);
    }
  };

  const overview = summary?.overview || {};
  const services = summaryServices(summary);
  const configuredProviders = useMemo(() => providersFromConfig(config, summary?.config), [config, summary?.config]);
  const visibleRunCount = runs.length;
  const stageEntries = useMemo(() => Object.entries(summary?.stages || {}) as [string, ManageStageBucket][], [summary?.stages]);
  const pressure = summary?.pressure || {};
  const cpuHistory = pressureHistory.map((item) => item.cpu);
  const memoryHistory = pressureHistory.map((item) => item.memory);
  const diskHistory = pressureHistory.map((item) => item.disk);
  const normalizedRunDetail = useMemo(() => normalizeRunDetail(runDetail), [runDetail]);
  const deploymentSteps = useMemo(() => Array.isArray(deploymentPlan?.steps) ? deploymentPlan.steps : [], [deploymentPlan]);
  const firstPageSteps = useMemo(
    () => deploymentSteps.filter((step) => Boolean(step.requiredForFirstPageLogin)),
    [deploymentSteps]
  );
  const dashboardConfigSteps = useMemo(
    () => deploymentSteps.filter((step) => Boolean(step.canConfigureInDashboard)),
    [deploymentSteps]
  );

  const serviceStats = useMemo(() => {
    const healthy = services.filter((service) => String(service.status) === 'ok').length;
    const degraded = services.filter((service) => String(service.status) === 'error').length;
    const local = services.filter((service) => String(service.mode) === 'local').length;
    const remote = services.filter((service) => String(service.mode) === 'remote').length;
    return { healthy, degraded, local, remote };
  }, [services]);

  const billingSummary = billingOps?.summary || {};
  const paymentSummary = (billingSummary.payments || {}) as Record<string, any>;
  const ledgerSummary = (billingSummary.ledger || {}) as Record<string, any>;
  const emailSummary = (billingSummary.emails || {}) as Record<string, any>;
  const billingCards = useMemo<MetricCardData[]>(() => {
    const totalPayments = Math.max(1, Number(paymentSummary.total || 0));
    const totalEmails = Math.max(1, Number(emailSummary.total || 0));
    return [
      {
        label: '实收金额',
        value: formatMoney(paymentSummary.totalPaidCents, 'usd'),
        footnote: `paid ${formatNumber(paymentSummary.paid)} / total ${formatNumber(paymentSummary.total)}`,
        percent: clampPercent((Number(paymentSummary.paid || 0) / totalPayments) * 100),
        tone: 'ok',
      },
      {
        label: '退款金额',
        value: formatMoney(paymentSummary.totalRefundedCents, 'usd'),
        footnote: `refund records ${formatNumber(paymentSummary.refunded)}`,
        percent: clampPercent((Math.abs(Number(paymentSummary.totalRefundedCents || 0)) / Math.max(1, Number(paymentSummary.totalPaidCents || 0))) * 100),
        tone: Number(paymentSummary.refunded || 0) > 0 ? 'warn' : 'ok',
      },
      {
        label: '用户余额',
        value: formatNumber(ledgerSummary.balanceCredits),
        footnote: `reserved ${formatNumber(ledgerSummary.reservedCredits)} credits`,
        percent: clampPercent((Number(ledgerSummary.reservedCredits || 0) / Math.max(1, Number(ledgerSummary.balanceCredits || 0))) * 100),
        tone: 'info',
      },
      {
        label: '购买额度',
        value: formatNumber(paymentSummary.totalCreditsPurchased),
        footnote: `ledger rows ${formatNumber(ledgerSummary.total)}`,
        percent: clampPercent((Number(paymentSummary.totalCreditsPurchased || 0) / Math.max(1, Number(ledgerSummary.balanceCredits || 0) + Number(paymentSummary.totalCreditsPurchased || 0))) * 100),
        tone: 'info',
      },
      {
        label: '邮件送达',
        value: formatNumber(emailSummary.delivered),
        footnote: `total ${formatNumber(emailSummary.total)}`,
        percent: clampPercent((Number(emailSummary.delivered || 0) / totalEmails) * 100),
        tone: Number(emailSummary.failed || 0) > 0 ? 'warn' : 'ok',
      },
      {
        label: '邮件失败',
        value: formatNumber(emailSummary.failed),
        footnote: 'failed / bounced / complained',
        percent: clampPercent((Number(emailSummary.failed || 0) / totalEmails) * 100),
        tone: Number(emailSummary.failed || 0) > 0 ? 'danger' : 'ok',
      },
    ];
  }, [emailSummary, ledgerSummary, paymentSummary]);

  const overviewCards = useMemo<MetricCardData[]>(() => {
    const totalTasks = Math.max(1, Number(overview.totalTasks || 0));
    const totalTokens = Math.max(1, Number(overview.totalTokens || 0));
    const totalCalls = Math.max(1, Number(overview.totalCalls || 0));
    const embeddingCalls = Number(summary?.usage?.embedding?.calls || 0);
    const rerankCalls = Number(summary?.usage?.rerank?.calls || 0);
    const llmCalls = Number(summary?.usage?.llm?.calls || 0);
    const maxCategoryCalls = Math.max(1, embeddingCalls, rerankCalls, llmCalls);

    return [
      {
        label: '今日任务',
        value: formatNumber(overview.todayTasks),
        footnote: `全部任务 ${formatNumber(overview.totalTasks)}`,
        percent: clampPercent((Number(overview.todayTasks || 0) / totalTasks) * 100),
        tone: 'info',
      },
      {
        label: '运行中',
        value: formatNumber(overview.runningTasks),
        footnote: `排队 ${formatNumber(overview.queuedTasks)}`,
        percent: clampPercent(((Number(overview.runningTasks || 0) + Number(overview.queuedTasks || 0)) / totalTasks) * 100),
        tone: Number(overview.runningTasks || 0) > 0 ? 'warn' : 'ok',
      },
      {
        label: '失败任务',
        value: formatNumber(overview.failedTasks),
        footnote: `调用总数 ${formatNumber(overview.totalCalls)}`,
        percent: clampPercent((Number(overview.failedTasks || 0) / totalTasks) * 100),
        tone: Number(overview.failedTasks || 0) > 0 ? 'danger' : 'ok',
      },
      {
        label: '今日 Tokens',
        value: formatCompactNumber(overview.todayTokens),
        footnote: `累计 ${formatCompactNumber(overview.totalTokens)}`,
        percent: clampPercent((Number(overview.todayTokens || 0) / totalTokens) * 100),
        tone: Number(overview.todayTokens || 0) > 0 ? 'info' : 'ok',
      },
      {
        label: '今日 Calls',
        value: formatNumber(overview.todayCalls),
        footnote: `累计 ${formatNumber(overview.totalCalls)}`,
        percent: clampPercent((Number(overview.todayCalls || 0) / totalCalls) * 100),
        tone: 'info',
      },
      {
        label: 'Embedding',
        value: formatNumber(embeddingCalls),
        footnote: `tokens ${formatCompactNumber(summary?.usage?.embedding?.tokens || 0)}`,
        percent: clampPercent((embeddingCalls / maxCategoryCalls) * 100),
        tone: 'ok',
      },
      {
        label: 'Rerank',
        value: formatNumber(rerankCalls),
        footnote: `tokens ${formatCompactNumber(summary?.usage?.rerank?.tokens || 0)}`,
        percent: clampPercent((rerankCalls / maxCategoryCalls) * 100),
        tone: 'warn',
      },
      {
        label: 'LLM',
        value: formatNumber(llmCalls),
        footnote: `tokens ${formatCompactNumber(summary?.usage?.llm?.tokens || 0)}`,
        percent: clampPercent((llmCalls / maxCategoryCalls) * 100),
        tone: 'info',
      },
    ];
  }, [overview, summary?.usage]);

  const databaseCards = useMemo<MetricCardData[]>(() => {
    const counts = (databaseAudit?.database?.counts || {}) as Record<string, any>;
    const business = (databaseAudit?.database?.business || {}) as Record<string, any>;
    const storage = (databaseAudit?.database?.storage || {}) as Record<string, any>;
    const cache = databaseAudit?.cache || {};
    const capacity = (databaseAudit?.capacity || {}) as Record<string, any>;
    const perRun = (capacity.perRunEstimate || {}) as Record<string, any>;
    const dailyBudget = (capacity.dailyRunBudget || {}) as Record<string, any>;
    const basis = (capacity.basis || {}) as Record<string, any>;
    const searchCacheCount = Number(cache.search?.count ?? counts.cacheEntries ?? 0);
    const runtimeTotal = Number(counts.runLogs || 0) + Number(counts.runEvents || 0) + Number(counts.runCheckpoints || 0);
    const businessTotal = Number(business.runs || 0)
      + Number(business.papers || 0)
      + Number(business.reviews || 0)
      + Number(business.citations || 0)
      + Number(business.artifacts || 0);
    const storageTotal = Math.max(1, Number(storage.totalBytes || 0));
    const storageKind = String(storage.kind || 'database');
    const storageSource = storage.source === 'pg_database_size' ? 'PostgreSQL' : storageKind === 'sqlite' ? 'SQLite 文件' : '数据库';
    const storageFootnote = storage.estimated
      ? `${storageSource}大小未知 · 本机剩余 ${formatBytes(storage.freeBytes || 0)}`
      : `${storageSource} · 本机剩余 ${formatBytes(storage.freeBytes || 0)}`;
    return [
      {
        label: '业务对象',
        value: formatNumber(businessTotal),
        footnote: `任务 ${formatNumber(business.runs || 0)} · 论文 ${formatNumber(business.papers || 0)} · 综述 ${formatNumber(business.reviews || 0)} · 引文 ${formatNumber(business.citations || 0)} · 产物 ${formatNumber(business.artifacts || 0)}`,
        percent: clampPercent((businessTotal / Math.max(1, businessTotal + Number(counts.searchResults || 0))) * 100),
        tone: 'info',
      },
      {
        label: '数据库占用',
        value: formatBytes(storage.usedBytes || 0),
        footnote: storageFootnote,
        percent: clampPercent((Number(storage.usedBytes || 0) / storageTotal) * 100),
        tone: Number(storage.diskUsedPercent || 0) >= 90 ? 'danger' : Number(storage.diskUsedPercent || 0) >= 80 ? 'warn' : 'ok',
      },
      {
        label: 'Runs',
        value: formatNumber(counts.runs || 0),
        footnote: `Logs ${formatNumber(counts.runLogs || 0)} · Events ${formatNumber(counts.runEvents || 0)} · Checkpoints ${formatNumber(counts.runCheckpoints || 0)}`,
        percent: clampPercent((Number(counts.runs || 0) / Math.max(1, Number(counts.runs || 0) + Number(counts.runLogs || 0))) * 100),
        tone: 'info',
      },
      {
        label: 'Task Cache',
        value: formatNumber(searchCacheCount),
        footnote: `${String(cache.search?.namespace || 'search')} · ${formatBytes(cache.search?.sizeBytes || 0)} · 任务内缓存，完成后清空`,
        percent: clampPercent((searchCacheCount / Math.max(1, searchCacheCount + 100)) * 100),
        tone: 'ok',
      },
      {
        label: 'JCR / KG Index',
        value: formatNumber(counts.kgPaperCache || 0),
        footnote: `${cache.kgPaperCacheEnabled ? 'enabled' : 'disabled this round'} · JCR 小索引在 D1，完整 JCRData 保留在仓库固定资产`,
        percent: clampPercent(Number(counts.kgPaperCache || 0)),
        tone: Number(counts.kgPaperCache || 0) > 0 ? 'warn' : 'info',
      },
      {
        label: 'Review Drafts',
        value: formatNumber(counts.reviewDrafts || 0),
        footnote: `Artifacts ${formatNumber(counts.runArtifacts || 0)} · 运行时对象 ${formatNumber(runtimeTotal)}`,
        percent: clampPercent((Number(counts.reviewDrafts || 0) / Math.max(1, Number(counts.reviewDrafts || 0) + Number(counts.runArtifacts || 0))) * 100),
        tone: 'warn',
      },
      {
        label: 'Compute Metrics',
        value: formatNumber(counts.computeJobMetrics || 0),
        footnote: 'Cloudflare Rust/WASM worker timings · BM25/vector/kNN/KG normalize',
        percent: clampPercent((Number(counts.computeJobMetrics || 0) / Math.max(1, Number(counts.computeJobs || 0) + Number(counts.computeJobMetrics || 0))) * 100),
        tone: Number(counts.computeJobMetrics || 0) > 0 ? 'ok' : 'info',
      },
      {
        label: 'Audit Logs',
        value: formatNumber(counts.adminAuditLogs || databaseAudit?.auditLogs?.length || 0),
        footnote: `最近 ${formatNumber(databaseAudit?.auditLogs?.length || 0)} 条 · cleanup/配置/账务审计`,
        percent: clampPercent((Number(counts.adminAuditLogs || 0) / Math.max(1, Number(counts.adminAuditLogs || 0) + Number(counts.runs || 0))) * 100),
        tone: 'info',
      },
      {
        label: '单任务 D1 写入',
        value: formatRange(perRun.d1RowsWrittenRange),
        footnote: `${formatNumber(basis.searchCandidates || 5000)} 候选 · ${formatNumber(basis.cappedPapers || 500)} 深处理`,
        percent: clampPercent((Number(Array.isArray(perRun.d1RowsWrittenRange) ? perRun.d1RowsWrittenRange[1] : perRun.d1RowsWrittenRange || 0) / 100000) * 100),
        tone: 'warn',
      },
      {
        label: '单任务 KV 分片',
        value: formatRange(perRun.artifactKvChunksRange || [0, 0]),
        footnote: `${formatRange(perRun.artifactBytesRange, formatBytes)} · snapshot/compute/artifact`,
        percent: clampPercent((Number((Array.isArray(perRun.artifactKvChunksRange) ? perRun.artifactKvChunksRange[1] : 0) || 0) / 1000) * 100),
        tone: 'ok',
      },
      {
        label: '免费层日预算',
        value: formatNumber(dailyBudget.safeDailyRuns || 0),
        footnote: `瓶颈 ${String(dailyBudget.limitingFactor || 'unknown')} · Queue ${formatNumber(dailyBudget.byQueueOps || 0)} runs/day · 并发是动态建议值`,
        percent: clampPercent((Number(dailyBudget.safeDailyRuns || 0) / Math.max(1, Number(dailyBudget.byQueueOps || dailyBudget.safeDailyRuns || 1))) * 100),
        tone: Number(dailyBudget.safeDailyRuns || 0) <= 3 ? 'danger' : 'info',
      },
    ];
  }, [databaseAudit]);

  const maxStageLoad = useMemo(() => {
    const values = stageEntries.map(([, stage]) => Number(stage.running || 0) + Number(stage.queued || 0));
    return Math.max(1, ...values);
  }, [stageEntries]);

  if (!authenticated) {
    return (
      <section className="manage-login route-shell">
        <div className="route-card manage-login-card">
          <p className="route-kicker">Management Console</p>
          <h2 className="route-title">登录</h2>
          {error && <div className="manage-error">{error}</div>}

          <div className="manage-login-switch" role="tablist" aria-label="登录方式">
            <button
              className={loginMode === 'user' ? 'active' : ''}
              onClick={() => setLoginMode('user')}
              type="button"
              role="tab"
              aria-selected={loginMode === 'user'}
            >
              用户
            </button>
            <button
              className={loginMode === 'admin' ? 'active' : ''}
              onClick={() => setLoginMode('admin')}
              type="button"
              role="tab"
              aria-selected={loginMode === 'admin'}
            >
              管理员
            </button>
          </div>

          <div className="manage-login-grid">
            <div className={`manage-login-pane ${loginMode === 'user' ? 'active' : ''}`}>
              <p className="route-kicker">User</p>
              <h3>用户登录 / 注册</h3>
              <input className="manage-input" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} placeholder="邮箱" type="email" />
              <input className="manage-input" value={userPassword} onChange={(e) => setUserPassword(e.target.value)} placeholder="密码" type="password" />
              <div className="manage-actions">
                <button className="manage-primary" disabled={busy || !supabaseConfigured} onClick={() => void handleUserSignIn()}>用户登录</button>
                <button className="manage-secondary" disabled={busy || !supabaseConfigured} onClick={() => void handleUserSignUp()}>注册用户</button>
              </div>
            </div>
            <div className={`manage-login-pane ${loginMode === 'admin' ? 'active' : ''}`}>
              <p className="route-kicker">Admin</p>
              <h3>管理员后台</h3>
              <input className="manage-input" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="管理 key" type="password" />
              <div className="manage-actions">
                <button className="manage-primary" disabled={busy} onClick={handleLogin}>进入后台</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="manage-shell">
      <aside className="manage-sidebar">
        <div>
          <p className="route-kicker">Admin</p>
          <h1 className="manage-title">管理后台</h1>
          <p className="manage-subtitle">任务、成本、配置与服务健康。</p>
          <div className="manage-live-card">
            <div>
              <span className={`manage-live-badge ${refreshing ? 'refreshing' : ''}`}>
                <i /> 实时监控
              </span>
              <p className="manage-live-copy">最近更新 {formatIsoTime(summary?.updatedAt)}</p>
            </div>
            <select
              className="manage-refresh-select"
              value={autoRefreshMs}
              onChange={(e) => setAutoRefreshMs(Number(e.target.value) || 10000)}
              aria-label="auto refresh interval"
            >
              {REFRESH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
        </div>
        <nav className="manage-tabs">
          {([
            ['overview', 'Overview'],
            ['runs', 'Runs'],
            ['users', 'Users'],
            ['billing', 'Billing'],
            ['config', 'Config'],
            ['services', 'Services'],
            ['database', 'Database'],
          ] as [TabKey, string][]).map(([key, label]) => (
            <button key={key} className={tab === key ? 'active' : ''} onClick={() => setTab(key)}>{label}</button>
          ))}
        </nav>
        <button
          className="manage-secondary"
          onClick={() => {
            if (tab === 'database') {
              void loadDatabaseAudit(manageKey);
              return;
            }
            if (tab === 'services') {
              void loadServices(manageKey);
              return;
            }
            if (tab === 'config') {
              void Promise.allSettled([
                loadConfig(manageKey),
                loadProviderUsage(manageKey),
              ]);
              return;
            }
            if (tab === 'users') {
              void loadUsers(manageKey);
              return;
            }
            if (tab === 'billing') {
              void loadBillingOps(manageKey);
              return;
            }
            void refreshAll();
          }}
          disabled={busy}
        >
          Refresh
        </button>
        <button
          className="manage-secondary"
          onClick={() => {
            sessionStorage.removeItem('manageKey');
            sessionStorage.removeItem('manageRoleSession');
            setManageKey('');
            setManageAccess(false);
            setKeyInput('');
          }}
          disabled={busy}
        >
          退出后台
        </button>
      </aside>

      <main className="manage-main">
        {bootMessage && <div className="manage-notice">{bootMessage}</div>}
        {error && <div className="manage-error">{error}</div>}

        {tab === 'overview' && (
          <section className="manage-section manage-overview-section">
            <header className="manage-section-header manage-section-header-rich">
              <div>
                <p className="route-kicker">Mission Control</p>
                <h2>总览导航台</h2>
                <p className="manage-tab-note">把任务、压力、阶段拥堵和服务健康放到同一视野里，减少在 JSON 与日志之间来回切换。</p>
              </div>
              <div className="manage-header-side">
                <span className="manage-muted">更新：{formatIsoTime(summary?.updatedAt)}</span>
                <span className={`manage-live-badge ${refreshing ? 'refreshing' : ''}`}><i /> 自动 {autoRefreshMs / 1000}s</span>
              </div>
            </header>

            {summaryError && <div className="manage-inline-error">总览刷新失败：{summaryError}</div>}

            <div className="manage-card-grid manage-card-grid-rich">
              {overviewCards.map((card) => <MetricCard key={card.label} card={card} />)}
            </div>

            <div className="manage-overview-grid">
              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Pressure</p>
                    <h3>资源压力</h3>
                  </div>
                  <span className="manage-panel-meta">最近 {pressureHistory.length || 1} 次采样</span>
                </div>
                <div className="manage-pressure-grid">
                  <PressureTile
                    label="CPU"
                    value={Number(pressure.cpuPercent || 0)}
                    unit="%"
                    tone={pressureTone(Number(pressure.cpuPercent || 0), 'cpu')}
                    detail="瞬时利用率"
                    history={cpuHistory.length > 0 ? cpuHistory : [Number(pressure.cpuPercent || 0)]}
                  />
                  <PressureTile
                    label="Memory"
                    value={Number(pressure.memoryPercent || 0)}
                    unit="%"
                    tone={pressureTone(Number(pressure.memoryPercent || 0), 'memory')}
                    detail={`${formatDecimal(pressure.memoryUsedGb, 2)} / ${formatDecimal(pressure.memoryTotalGb, 2)} GB`}
                    history={memoryHistory.length > 0 ? memoryHistory : [Number(pressure.memoryPercent || 0)]}
                  />
                  <PressureTile
                    label="Disk"
                    value={Number(pressure.diskPercent || 0)}
                    unit="%"
                    tone={pressureTone(Number(pressure.diskPercent || 0), 'disk')}
                    detail={`剩余 ${formatDecimal(pressure.diskFreeGb, 2)} GB`}
                    history={diskHistory.length > 0 ? diskHistory : [Number(pressure.diskPercent || 0)]}
                  />
                </div>
              </div>

              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Queue Lanes</p>
                    <h3>阶段队列</h3>
                  </div>
                  <span className="manage-panel-meta">按活跃密度排序</span>
                </div>
                <div className="manage-stage-lanes">
                  {stageEntries.map(([key, stage]) => {
                    const load = Number(stage.running || 0) + Number(stage.queued || 0);
                    return <StageLane key={key} stage={stage} loadPercent={(load / maxStageLoad) * 100} />;
                  })}
                </div>
              </div>

              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Service Board</p>
                    <h3>模型与服务健康</h3>
                  </div>
                  <span className="manage-panel-meta">健康 {serviceStats.healthy} · 异常 {serviceStats.degraded}</span>
                </div>
                {servicesRefreshing && <div className="manage-inline-muted">服务健康后台刷新中...</div>}
                {servicesError && <div className="manage-inline-error">服务健康刷新失败：{servicesError}</div>}
                <div className="manage-service-summary-grid">
                  <div className="manage-service-summary-card tone-ok"><span>Healthy</span><strong>{formatNumber(serviceStats.healthy)}</strong></div>
                  <div className="manage-service-summary-card tone-danger"><span>Degraded</span><strong>{formatNumber(serviceStats.degraded)}</strong></div>
                  <div className="manage-service-summary-card tone-info"><span>Remote</span><strong>{formatNumber(serviceStats.remote)}</strong></div>
                  <div className="manage-service-summary-card tone-warn"><span>Local</span><strong>{formatNumber(serviceStats.local)}</strong></div>
                </div>
                <div className="manage-service-stack">
                  {services.slice(0, 4).map((service) => (
                    <ServiceCard key={`${service.name}-${service.url || service.backend || service.model || ''}`} service={service} />
                  ))}
                </div>
              </div>

              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Ops Notes</p>
                    <h3>运维建议</h3>
                  </div>
                  <span className="manage-panel-meta">自动汇总</span>
                </div>
                <div className="manage-recommendation-list">
                  {(summary?.recommendations || []).map((item) => (
                    <div className="manage-recommendation-item" key={item}>{item}</div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === 'runs' && (
          <section className="manage-section manage-runs-layout">
            <div className="manage-card manage-run-list">
              <h2>任务历史</h2>
              <div className="manage-inline-muted">首屏 {formatNumber(visibleRunCount)} 条，按需继续加载。</div>
              {runsError && <div className="manage-inline-error">任务历史刷新失败：{runsError}</div>}
              {runsRefreshing && <div className="manage-inline-muted">正在刷新任务列表...</div>}
              {runs.map((run) => {
                const id = runIdentity(run);
                const tokenOrCredit = run.tokenUsage ?? run.totalTokens ?? run.reserved_credits ?? run.reservedCredits ?? 0;
                const modelOrStage = run.llm || run.current_step || run.currentStage || 'pipeline';
                return (
                  <button key={id} className={`manage-run-item ${selectedRunId === id ? 'active' : ''}`} onClick={() => handleSelectRun(id)}>
                    <span className={`manage-status ${statusClass(run.status)}`}>{String(run.status || 'unknown')}</span>
                    <strong>{runTitle(run)}</strong>
                    <small>{formatIsoTime(runCreatedAt(run))} · {displayText(modelOrStage)} · {formatNumber(tokenOrCredit)} {run.tokenUsage || run.totalTokens ? 'tokens' : 'credits'}</small>
                  </button>
                );
              })}
              {runsHasMore && (
                <button className="manage-secondary" onClick={() => void handleLoadMoreRuns()} disabled={runsRefreshing}>
                  加载更多 20 条
                </button>
              )}
            </div>
            <div className="manage-card manage-run-detail">
              {detailError && <div className="manage-inline-error">任务详情刷新失败：{detailError}</div>}
              {detailRefreshing && <div className="manage-inline-muted">正在加载任务详情...</div>}
              {!normalizedRunDetail && !detailRefreshing && <p className="manage-muted">选择一个任务查看文献、日志、产物和消耗。</p>}
              {normalizedRunDetail && (
                <>
                  <div className="manage-detail-header">
                    <div>
                      <span className={`manage-status ${statusClass(normalizedRunDetail.status)}`}>{displayText(normalizedRunDetail.status)}</span>
                      <h2>{displayText(normalizedRunDetail.title)}</h2>
                      <p className="manage-muted">{displayText(normalizedRunDetail.id)}</p>
                    </div>
                    <div className="manage-actions">
                      <button className="manage-secondary" onClick={() => void handleRunAction('resume')} disabled={busy}>恢复/重试</button>
                      <button className="manage-danger" onClick={() => void handleRunAction('cancel')} disabled={busy}>取消</button>
                    </div>
                  </div>
                  <div className="manage-mini-grid">
                    <div><span>Token</span><strong>{formatNumber(normalizedRunDetail.tokenUsage)}</strong></div>
                    <div><span>调用</span><strong>{formatNumber(normalizedRunDetail.calls)}</strong></div>
                    <div><span>当前阶段</span><strong>{displayText(normalizedRunDetail.currentStage)}</strong></div>
                    <div><span>预扣额度</span><strong>{formatNumber(normalizedRunDetail.credits.reserved)}</strong></div>
                    <div><span>实际额度</span><strong>{formatNumber(normalizedRunDetail.credits.actual)}</strong></div>
                    <div><span>更新时间</span><strong>{formatIsoTime(normalizedRunDetail.updatedAt)}</strong></div>
                  </div>
                  {normalizedRunDetail.error && <div className="manage-inline-error">{String(normalizedRunDetail.error)}</div>}
                  <h3>阶段</h3>
                  <div className="manage-stage-list">
                    {normalizedRunDetail.stages.length > 0
                      ? normalizedRunDetail.stages.map((stage: any) => <div key={displayText(stage.key || stage.stage || stage.label)}><span className={`manage-status ${statusClass(stage.status)}`}>{displayText(stage.status, 'unknown')}</span><strong>{displayText(stage.label || stage.stage)}</strong><small>{displayText(stage.message)}</small></div>)
                      : <div><span className={`manage-status ${statusClass(normalizedRunDetail.status)}`}>{displayText(normalizedRunDetail.status)}</span><strong>{displayText(normalizedRunDetail.currentStage)}</strong><small>{displayText(normalizedRunDetail.latestErrorEvent?.message, '暂无阶段明细，查看下方事件日志。')}</small></div>}
                  </div>
                  <h3>Rust Compute Jobs</h3>
                  <div className="manage-stage-list">
                    {normalizedRunDetail.computeJobs.length > 0
                      ? normalizedRunDetail.computeJobs.map((job: any) => (
                        <div key={job.id || `${job.operation}-${job.input_key}`}>
                          <span className={`manage-status ${statusClass(job.status)}`}>{String(job.status || 'unknown')}</span>
                          <strong>{String(job.operation || 'compute')}</strong>
                          <small>{job.lease_owner ? `worker ${displayText(job.lease_owner)} · ` : ''}{displayText(job.error || job.output_key || job.input_key)}</small>
                        </div>
                      ))
                      : <div><span className="manage-status queued">empty</span><strong>暂无本地计算任务</strong><small>搜索分发后会生成 dedup / cluster 等 compute job。</small></div>}
                  </div>
                  <h3>本地计算热点</h3>
                  {hasRunPerformance(normalizedRunDetail.computePerformance) && (
                    <div className="manage-mini-grid">
                      <div>
                        <span>Compute Queue Wall</span>
                        <strong>{formatMs(performanceTotals(normalizedRunDetail.computePerformance, 'compute').totalDurationMs)}</strong>
                      </div>
                      <div>
                        <span>Rust/WASM Wall</span>
                        <strong>{formatMeasuredMs(performanceTotals(normalizedRunDetail.computePerformance, 'compute').nativeWallMs, { samples: runNativeStageRows(normalizedRunDetail.computePerformance).length })}</strong>
                      </div>
                      <div>
                        <span>Provider Wait</span>
                        <strong>{formatMs(performanceTotals(normalizedRunDetail.computePerformance, 'providers').totalLatencyMs)}</strong>
                      </div>
                      <div>
                        <span>Source Wait</span>
                        <strong>{formatMs(performanceTotals(normalizedRunDetail.computePerformance, 'sources').totalLatencyMs)}</strong>
                      </div>
                      <div>
                        <span>Tokens</span>
                        <strong>{formatNumber(performanceTotals(normalizedRunDetail.computePerformance, 'providers').totalTokens)}</strong>
                      </div>
                      <div>
                        <span>Compute Jobs</span>
                        <strong>{formatNumber(performanceTotals(normalizedRunDetail.computePerformance, 'compute').jobs)}</strong>
                      </div>
                    </div>
                  )}
                  <NativeRuntimeStrip rows={runNativeStageRows(normalizedRunDetail.computePerformance).length > 0
                    ? runNativeStageRows(normalizedRunDetail.computePerformance)
                    : performanceRows(normalizedRunDetail.computePerformance, 'rustCandidates')} />
                  <h3>Compute Queue Wall</h3>
                  <div className="manage-stage-list">
                    {runOperationRows(normalizedRunDetail.computePerformance).length > 0
                      ? runOperationRows(normalizedRunDetail.computePerformance).slice(0, 8).map((row: any) => (
                        <div key={`${row.operation}-queue-wall`}>
                          <span className={`manage-status ${statusClass(row.failed ? 'failed' : row.running ? 'running' : 'ok')}`}>{String(row.operation || 'compute')}</span>
                          <strong>queue-to-complete {formatMs(row.totalDurationMs)}</strong>
                          <small>
                            jobs {formatNumber(row.jobs)} · completed {formatNumber(row.completed)}
                            · running {formatNumber(row.running)} · queued {formatNumber(row.queued)}
                            · failed {formatNumber(row.failed)} · avg {formatMs(row.avgDurationMs)}
                          </small>
                        </div>
                      ))
                      : <div><span className="manage-status queued">empty</span><strong>暂无 compute job wall time</strong><small>这个指标包含排队、Cloudflare Compute Worker 执行、D1 artifact 分片读写和算法计算，不等同于 CPU 时间。</small></div>}
                  </div>
                  <h3>外部等待</h3>
                  <ExternalWaitTimeline rows={externalWaitRows(normalizedRunDetail.computePerformance)} />
                  <h3>运行时热力图</h3>
                  <RuntimeHeatmapGrid rows={nativeRuntimeHeatmapRows(normalizedRunDetail.computePerformance)} />
                  <p className="manage-muted">Rust/WASM 阶段为 Worker 内部自报耗时；Cloudflare 对极短同步计算可能返回 0 ms，界面按低于计时分辨率显示为 &lt;1 ms。</p>
                  <h3>文献内容</h3>
                  <div className="manage-paper-list">
                    {normalizedRunDetail.literature.length > 0
                      ? normalizedRunDetail.literature.slice(0, 30).map((paper: any, index: number) => <article key={`${displayText(paper.doi || paper.title)}-${index}`}><strong>{displayText(paper.title, 'Untitled')}</strong><small>{displayText(paper.year)} · {displayText(paper.venue)} · {displayText(paper.doi)}</small><p>{displayText(paper.abstract, '暂无摘要')}</p></article>)
                      : <article><strong>Edge MVP 不在详情页展开大论文 payload</strong><small>大 JSON 保存在 D1 artifact 分片，paper_index/run_papers 保留轻索引和 artifact 元数据。</small><p>后续可按 artifact 或 search snapshot 继续补详情加载。</p></article>}
                  </div>
                  <h3>产物</h3>
                  <ArtifactGrid artifacts={normalizedRunDetail.artifacts} />
                  <h3>日志预览</h3>
                  <pre className="manage-code">{normalizedRunDetail.logs.map((log: any) => displayText(log.message, '')).join('\n') || '暂无日志'}</pre>
                </>
              )}
            </div>
          </section>
        )}

        {tab === 'users' && (
          <section className="manage-section manage-runs-layout">
            <div className="manage-card manage-run-list">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Users</p>
                  <h2>用户管理</h2>
                </div>
                <span className="manage-panel-meta">{formatNumber(usersTotal)} users</span>
              </div>
              <div className="manage-provider-form-grid">
                <label><span>搜索</span><input className="manage-input" value={userQuery} onChange={(event) => setUserQuery(event.target.value)} placeholder="email / user id" /></label>
                <label><span>角色</span><select className="manage-input" value={userRoleFilter} onChange={(event) => setUserRoleFilter(event.target.value)}><option value="">全部</option><option value="user">user</option><option value="operator">operator</option><option value="admin">admin</option></select></label>
                <label><span>状态</span><select className="manage-input" value={userStatusFilter} onChange={(event) => setUserStatusFilter(event.target.value)}><option value="">全部</option><option value="active">active</option><option value="disabled">disabled</option></select></label>
              </div>
              <div className="manage-actions">
                <button className="manage-primary" onClick={() => void loadUsers(manageKey)} disabled={usersRefreshing}>查询用户</button>
                <button className="manage-secondary" onClick={() => { setUserQuery(''); setUserRoleFilter(''); setUserStatusFilter(''); void loadUsers(manageKey); }} disabled={usersRefreshing}>重置</button>
              </div>
              {usersError && <div className="manage-inline-error">用户列表刷新失败：{usersError}</div>}
              {usersRefreshing && <div className="manage-inline-muted">正在加载用户...</div>}
              {users.length === 0 && !usersRefreshing && <p className="manage-muted">暂无用户镜像。用户第一次登录 `/api/me` 后会写入 D1。</p>}
              {users.map((user) => (
                <button key={String(user.id)} className={`manage-run-item ${selectedUserId === user.id ? 'active' : ''}`} onClick={() => setSelectedUserId(String(user.id))}>
                  <span className={`manage-status ${statusClass(user.status)}`}>{String(user.status || 'unknown')}</span>
                  <strong>{String(user.email || user.id)}</strong>
                  <small>{String(user.role || 'user')} · {formatNumber(user.balance_credits)} credits · {formatNumber(user.run_count)} runs</small>
                </button>
              ))}
            </div>

            <div className="manage-card manage-run-detail">
              {selectedUserError && <div className="manage-inline-error">用户详情刷新失败：{selectedUserError}</div>}
              {!selectedUserId && <p className="manage-muted">选择一个用户查看角色、状态、额度和账本。</p>}
              {selectedUserId && (
                <>
                  {(() => {
                    const detailUser = ((selectedUser?.user as Record<string, any> | undefined) || users.find((user) => user.id === selectedUserId) || {}) as Record<string, any>;
                    const account = ((selectedUser?.credits as Record<string, any> | undefined)?.account || detailUser) as Record<string, any>;
                    const ledger = Array.isArray((selectedUser?.credits as Record<string, any> | undefined)?.ledger)
                      ? ((selectedUser?.credits as Record<string, any>).ledger as Array<Record<string, any>>)
                      : [];
                    return (
                      <>
                        <div className="manage-detail-header">
                          <div>
                            <span className={`manage-status ${statusClass(detailUser.status)}`}>{String(detailUser.status || 'unknown')}</span>
                            <h2>{String(detailUser.email || selectedUserId)}</h2>
                            <p className="manage-muted">{selectedUserId}</p>
                          </div>
                          <div className="manage-actions">
                            <button className="manage-secondary" onClick={() => void loadSelectedUser(selectedUserId, manageKey)} disabled={usersRefreshing}>刷新</button>
                          </div>
                        </div>
                        <div className="manage-mini-grid">
                          <div><span>角色</span><strong>{String(detailUser.role || '-')}</strong></div>
                          <div><span>状态</span><strong>{String(detailUser.status || '-')}</strong></div>
                          <div><span>余额</span><strong>{formatNumber(account.balance_credits)}</strong></div>
                          <div><span>预扣</span><strong>{formatNumber(account.reserved_credits)}</strong></div>
                          <div><span>任务</span><strong>{formatNumber(detailUser.run_count)}</strong></div>
                          <div><span>支付</span><strong>{formatNumber(detailUser.payment_count)}</strong></div>
                        </div>
                        <h3>权限与状态</h3>
                        <div className="manage-provider-form-grid">
                          <label><span>角色</span><select className="manage-input" value={String(detailUser.role || 'user')} onChange={(event) => void handleUpdateUser(selectedUserId, { role: event.target.value })} disabled={busy}><option value="user">user</option><option value="operator">operator</option><option value="admin">admin</option></select></label>
                          <label><span>状态</span><select className="manage-input" value={String(detailUser.status || 'active')} onChange={(event) => void handleUpdateUser(selectedUserId, { status: event.target.value })} disabled={busy}><option value="active">active</option><option value="disabled">disabled</option></select></label>
                        </div>
                        <h3>额度调整</h3>
                        <div className="manage-provider-form-grid">
                          <label><span>Credits</span><input className="manage-input" value={creditAdjustment.amountCredits} onChange={(event) => setCreditAdjustment((current) => ({ ...current, amountCredits: event.target.value }))} inputMode="numeric" /></label>
                          <label><span>原因</span><input className="manage-input" value={creditAdjustment.reason} onChange={(event) => setCreditAdjustment((current) => ({ ...current, reason: event.target.value }))} /></label>
                        </div>
                        <button className="manage-primary" onClick={() => void handleAdjustUserCredits()} disabled={busy}>写入额度账本</button>
                        <h3>最近账本</h3>
                        <div className="manage-stage-list">
                          {ledger.length > 0
                            ? ledger.slice(0, 10).map((row) => (
                              <div key={String(row.id)}>
                                <span className="manage-status info">{String(row.type || 'ledger')}</span>
                                <strong>{formatNumber(row.amount_credits)} credits</strong>
                                <small>{String(row.description || row.payment_id || row.run_id || '-')}</small>
                              </div>
                            ))
                            : <div><span className="manage-status queued">empty</span><strong>暂无账本</strong><small>充值、任务结算或后台调整后会写入这里。</small></div>}
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
          </section>
        )}

        {tab === 'billing' && (
          <section className="manage-section">
            <header className="manage-section-header manage-section-header-rich">
              <div>
                <p className="route-kicker">Billing Ops</p>
                <h2>账务与邮件运营</h2>
                <p className="manage-tab-note">集中检查 Stripe 支付、额度账本和 Resend 投递回写，确保用户历史、支付记录和通知状态都落在 D1。</p>
              </div>
              <div className="manage-header-side">
                <span className="manage-muted">更新于 {formatIsoTime(billingOps?.updatedAt)}</span>
                <button className="manage-secondary" onClick={() => void loadBillingOps(manageKey)} disabled={billingRefreshing}>刷新运营数据</button>
              </div>
            </header>
            {billingError && <div className="manage-inline-error">账务运营刷新失败：{billingError}</div>}
            {billingRefreshing && <div className="manage-inline-muted">正在加载 Stripe / Resend 运营数据...</div>}

            <div className="manage-card-grid manage-card-grid-rich">
              {billingCards.map((card) => <MetricCard key={card.label} card={card} />)}
            </div>

            <div className="manage-two-col">
              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Stripe Payments</p>
                    <h3>支付记录</h3>
                  </div>
                  <span className="manage-panel-meta">{formatNumber(billingOps?.paymentsTotal)} records</span>
                </div>
                <div className="manage-stage-list">
                  {(billingOps?.payments || []).length > 0
                    ? (billingOps?.payments || []).slice(0, 20).map((payment) => (
                      <div key={String(payment.id)}>
                        <span className={`manage-status ${statusClass(payment.status)}`}>{String(payment.status || 'unknown')}</span>
                        <strong>{formatMoney(payment.amount_cents, payment.currency)} · {formatNumber(payment.credits)} credits</strong>
                        <small>
                          {String(payment.user_email || payment.user_id || '-')} · {String(payment.provider || 'provider')}
                          {payment.provider_session_id ? ` · session ${String(payment.provider_session_id)}` : ''}
                          {payment.provider_payment_id ? ` · payment ${String(payment.provider_payment_id)}` : ''}
                          {' · '}{formatIsoTime(payment.created_at)}
                        </small>
                        {canRefundStripePayment(payment) && (
                          <span className="manage-row-actions">
                            <button
                              className="manage-danger manage-compact-button"
                              type="button"
                              disabled={busy || billingRefreshing}
                              onClick={() => void handleRefundPayment(payment)}
                            >
                              发起退款
                            </button>
                            <small>退款成功后的额度反记账等待 Stripe webhook 写入。</small>
                          </span>
                        )}
                      </div>
                    ))
                    : <div><span className="manage-status queued">empty</span><strong>暂无支付记录</strong><small>Stripe checkout 创建或 webhook 入账后会写入 payments。</small></div>}
                </div>
              </div>

              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Credit Ledger</p>
                    <h3>额度账本</h3>
                  </div>
                  <span className="manage-panel-meta">{formatNumber(billingOps?.ledgerTotal)} rows</span>
                </div>
                <div className="manage-stage-list">
                  {(billingOps?.ledger || []).length > 0
                    ? (billingOps?.ledger || []).slice(0, 20).map((row) => (
                      <div key={String(row.id)}>
                        <span className="manage-status info">{String(row.type || 'ledger')}</span>
                        <strong>{formatNumber(row.amount_credits)} credits · balance {formatNumber(row.balance_after)}</strong>
                        <small>
                          {String(row.user_email || row.user_id || '-')}
                          {row.run_topic ? ` · ${String(row.run_topic)}` : ''}
                          {row.payment_id ? ` · ${String(row.payment_provider || 'payment')} ${String(row.payment_status || '')}` : ''}
                          {' · '}{String(row.description || '-')} · {formatIsoTime(row.created_at)}
                        </small>
                      </div>
                    ))
                    : <div><span className="manage-status queued">empty</span><strong>暂无额度流水</strong><small>购买、退款、任务预扣/结算或管理员调整都会写入 credit_ledger。</small></div>}
                </div>
              </div>
            </div>

            <div className="manage-card manage-card-deep">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Resend Deliveries</p>
                  <h3>邮件投递</h3>
                </div>
                <span className="manage-panel-meta">{formatNumber(billingOps?.deliveriesTotal)} records</span>
              </div>
              <div className="manage-stage-list">
                {(billingOps?.deliveries || []).length > 0
                  ? (billingOps?.deliveries || []).slice(0, 30).map((delivery) => (
                    <div key={String(delivery.id)}>
                      <span className={`manage-status ${statusClass(delivery.status)}`}>{String(delivery.status || 'unknown')}</span>
                      <strong>{String(delivery.subject || 'email')}</strong>
                      <small>
                        to {String(delivery.to_email || '-')} · {String(delivery.user_email || delivery.user_id || '-')}
                        {delivery.run_topic ? ` · ${String(delivery.run_topic)}` : ''}
                        {delivery.provider_message_id ? ` · message ${String(delivery.provider_message_id)}` : ''}
                        {delivery.error ? ` · ${String(delivery.error)}` : ''}
                        {' · '}{formatIsoTime(delivery.updated_at || delivery.created_at)}
                      </small>
                    </div>
                  ))
                  : <div><span className="manage-status queued">empty</span><strong>暂无邮件投递</strong><small>任务结果邮件发送和 Resend webhook 回写后会显示在这里。</small></div>}
              </div>
            </div>

            <div className="manage-card manage-card-deep manage-email-test-card">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Resend Probe</p>
                  <h3>发送测试邮件</h3>
                </div>
                <span className="manage-panel-meta">POST /api/manage/emails/test</span>
              </div>
              <div className="manage-email-test-grid">
                <label>
                  <span>收件人</span>
                  <input
                    className="manage-input"
                    value={testEmailDraft.toEmail}
                    onChange={(event) => setTestEmailDraft((current) => ({ ...current, toEmail: event.target.value }))}
                    placeholder="smoke-user@example.com"
                    type="email"
                  />
                </label>
                <label>
                  <span>主题</span>
                  <input
                    className="manage-input"
                    value={testEmailDraft.subject}
                    onChange={(event) => setTestEmailDraft((current) => ({ ...current, subject: event.target.value }))}
                    placeholder="Academic Cluster Resend smoke"
                  />
                </label>
              </div>
              <label className="manage-email-body-field">
                <span>正文</span>
                <textarea
                  className="manage-textarea manage-email-test-textarea"
                  value={testEmailDraft.text}
                  onChange={(event) => setTestEmailDraft((current) => ({ ...current, text: event.target.value }))}
                  placeholder="Hello from Academic Cluster Edge management console."
                />
              </label>
              <div className="manage-row-actions">
                <button className="manage-primary" onClick={() => void handleSendTestEmail()} disabled={busy || billingRefreshing}>
                  发送测试邮件
                </button>
                <button
                  className="manage-secondary"
                  onClick={() => {
                    setTestEmailDraft(EMPTY_TEST_EMAIL_DRAFT);
                    setTestEmailResult(null);
                  }}
                  disabled={busy}
                >
                  重置
                </button>
                <small>成功后会写入 D1 email_deliveries，并等待 Resend webhook 回写 delivered/bounced/failed。</small>
              </div>
              {testEmailResult && (
                <div className="manage-email-test-result">
                  <span className={`manage-status ${statusClass((testEmailResult.delivery as Record<string, any> | undefined)?.status || 'sent')}`}>
                    {String((testEmailResult.delivery as Record<string, any> | undefined)?.status || 'sent')}
                  </span>
                  <strong>{String(testEmailResult.providerMessageId || '-')}</strong>
                  <small>delivery {String((testEmailResult.delivery as Record<string, any> | undefined)?.id || '-')}</small>
                </div>
              )}
            </div>

            <div className="manage-two-col">
              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Breakdown</p>
                    <h3>支付与账本分布</h3>
                  </div>
                </div>
                <div className="manage-stage-list">
                  {Array.isArray(paymentSummary.byStatus) && paymentSummary.byStatus.length > 0
                    ? paymentSummary.byStatus.map((row: any) => (
                      <div key={`payment-status-${String(row.status)}`}>
                        <span className={`manage-status ${statusClass(row.status)}`}>{String(row.status || 'status')}</span>
                        <strong>{formatNumber(row.count)} records · {formatMoney(row.amount_cents, 'usd')}</strong>
                        <small>{formatNumber(row.credits)} credits</small>
                      </div>
                    ))
                    : <div><span className="manage-status queued">empty</span><strong>暂无支付状态分布</strong><small>Stripe webhook 完成后会生成状态聚合。</small></div>}
                  {Array.isArray(ledgerSummary.byType) && ledgerSummary.byType.map((row: any) => (
                    <div key={`ledger-type-${String(row.type)}`}>
                      <span className="manage-status info">{String(row.type || 'type')}</span>
                      <strong>{formatNumber(row.amount_credits)} credits</strong>
                      <small>{formatNumber(row.count)} ledger rows</small>
                    </div>
                  ))}
                </div>
              </div>

              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Delivery Breakdown</p>
                    <h3>邮件状态分布</h3>
                  </div>
                </div>
                <div className="manage-stage-list">
                  {Array.isArray(emailSummary.byStatus) && emailSummary.byStatus.length > 0
                    ? emailSummary.byStatus.map((row: any) => (
                      <div key={`email-status-${String(row.status)}`}>
                        <span className={`manage-status ${statusClass(row.status)}`}>{String(row.status || 'status')}</span>
                        <strong>{formatNumber(row.count)} emails</strong>
                        <small>Resend webhook 会重复投递，后端按 provider message id 更新最新状态。</small>
                      </div>
                    ))
                    : <div><span className="manage-status queued">empty</span><strong>暂无邮件状态分布</strong><small>发送结果邮件后会开始累计。</small></div>}
                  {Array.isArray(emailSummary.byProvider) && emailSummary.byProvider.map((row: any) => (
                    <div key={`email-provider-${String(row.provider)}`}>
                      <span className="manage-status info">{String(row.provider || 'provider')}</span>
                      <strong>{formatNumber(row.count)} emails</strong>
                      <small>provider distribution</small>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {tab === 'config' && (
          <section className="manage-section">
            <header className="manage-section-header"><h2>接口与服务配置</h2></header>
            {configError && <div className="manage-inline-error">配置刷新失败：{configError}</div>}
            {providerUsageError && <div className="manage-inline-error">Provider 状态刷新失败：{providerUsageError}</div>}
            {sourceUsageError && <div className="manage-inline-error">Source 状态刷新失败：{sourceUsageError}</div>}
            {configRefreshing && <div className="manage-inline-muted">正在刷新配置...</div>}
            <ProviderConfigPanel
              providers={configuredProviders}
              usage={providerUsage}
              draft={providerDraft}
              setDraft={setProviderDraft}
              onSave={() => void handleSaveProvider()}
              onTest={() => void handleTestProvider()}
              onRefreshUsage={() => void loadProviderUsage(manageKey)}
              onToggleProvider={(providerId, enabled) => void handleToggleProvider(providerId, enabled)}
              onClearProviderCooldown={(providerId) => void handleClearProviderCooldown(providerId)}
              onDeleteProvider={(providerId) => void handleDeleteProvider(providerId)}
              onTestProvider={(row) => void handleTestConfiguredProvider(row)}
              testResult={providerTestResult}
              testError={providerTestError}
              testingProviderId={testingProviderId}
              busy={busy}
              usageRefreshing={providerUsageRefreshing}
            />
            <SourceOpsPanel
              usage={sourceUsage}
              draft={sourceDraft}
              setDraft={setSourceDraft}
              onSave={() => void handleSaveSource()}
              onTest={() => void handleTestSource()}
              onRefreshUsage={() => void loadSourceUsage(manageKey)}
              onToggleSource={(source, enabled) => void handleToggleSource(source, enabled)}
              busy={busy}
              usageRefreshing={sourceUsageRefreshing}
            />
            <div className="manage-two-col">
              <div className="manage-card">
                <h3>当前脱敏配置</h3>
                <pre className="manage-code manage-config-view">{JSON.stringify(config || {}, null, 2)}</pre>
                <h3>队列后端解析</h3>
                <p className="manage-muted">新主线不再依赖 Huey + Redis。Cloudflare-first 方案用 Workflows + Queues + D1 + CONFIG_KV + Durable Objects；这里保留的 patch 仅用于旧系统或实验环境。</p>
                <pre className="manage-code manage-config-view">{JSON.stringify(summary?.config?.taskQueue || {}, null, 2)}</pre>
              </div>
              <div className="manage-card">
                <h3>保存高级配置 patch</h3>
                <p className="manage-muted">常规模型节点优先使用上方表单；这里保留点路径 JSON 给队列、数据库、限流等高级配置。</p>
                <p className="manage-muted">示例：embedding.providers.append 可追加一个包含 MODEL、API_URL、API_KEY、RPS、CONCURRENCY 的 provider。</p>
                <p className="manage-muted">密码直接写进 URL 即可，后台会脱敏显示。</p>
                <p className="manage-muted">Cloudflare-first 主链路不依赖 PostgreSQL / Redis；JCRData 固定资产放 D1 小索引，完整原始库保留在仓库 jcrdata/，免费外部数据库只作对照环境。</p>
                <textarea className="manage-textarea" value={configPatch} onChange={(e) => setConfigPatch(e.target.value)} placeholder='{"task_queue.REDIS_URL":"redis://:password@127.0.0.1:6379/0","database.URL":"postgresql+asyncpg://user:password@db-host:5432/academic"}' />
                <button className="manage-primary" onClick={handleSaveConfig} disabled={busy}>保存并刷新运行时配置</button>
              </div>
            </div>
          </section>
        )}

        {tab === 'services' && (
          <section className="manage-section">
            <header className="manage-section-header manage-section-header-rich">
              <div>
                <p className="route-kicker">Service Matrix</p>
                <h2>服务健康与一键测试</h2>
                <p className="manage-tab-note">把模型、数据库、队列和外部接口拆成独立卡片，优先暴露延迟、错误和回退状态。</p>
              </div>
              <div className="manage-header-side">
                <span className="manage-muted">最近更新 {formatIsoTime(summary?.updatedAt)}</span>
                <button className="manage-secondary" onClick={() => void refreshAll()} disabled={busy}>立即刷新</button>
              </div>
            </header>

            {(summaryError || servicesError) && <div className="manage-inline-error">服务健康刷新失败：{summaryError || servicesError}</div>}
            {deploymentPlanError && <div className="manage-inline-error">部署计划刷新失败：{deploymentPlanError}</div>}

            <div className="manage-service-summary-grid manage-service-summary-grid-wide">
              <div className="manage-service-summary-card tone-ok"><span>Healthy</span><strong>{formatNumber(serviceStats.healthy)}</strong></div>
              <div className="manage-service-summary-card tone-danger"><span>Errors</span><strong>{formatNumber(serviceStats.degraded)}</strong></div>
              <div className="manage-service-summary-card tone-info"><span>Remote</span><strong>{formatNumber(serviceStats.remote)}</strong></div>
              <div className="manage-service-summary-card tone-warn"><span>Local</span><strong>{formatNumber(serviceStats.local)}</strong></div>
            </div>

            <div className="manage-card manage-card-deep">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Deployment Plan</p>
                  <h3>先上线页面，再补服务</h3>
                </div>
                <span className="manage-panel-meta">
                  ok {formatNumber(deploymentPlan?.counts?.ok || 0)} · queued {formatNumber(deploymentPlan?.counts?.queued || 0)} · errors {formatNumber(deploymentPlan?.counts?.error || 0)}
                </span>
              </div>
              <div className="manage-service-summary-grid manage-service-summary-grid-wide">
                <div className="manage-service-summary-card tone-info"><span>首屏必需</span><strong>{formatNumber(firstPageSteps.length)}</strong></div>
                <div className="manage-service-summary-card tone-ok"><span>后台可配</span><strong>{formatNumber(dashboardConfigSteps.length)}</strong></div>
                <div className="manage-service-summary-card tone-warn"><span>生产必需</span><strong>{formatNumber(deploymentPlan?.counts?.requiredForProduction || 0)}</strong></div>
                <div className="manage-service-summary-card tone-danger"><span>平台侧</span><strong>{formatNumber(Math.max(0, deploymentSteps.length - dashboardConfigSteps.length))}</strong></div>
              </div>
              <div className="manage-deploy-grid">
                {(deploymentSteps.length > 0 ? deploymentSteps : []).map((step) => (
                  <DeploymentStepCard key={String(step.id || step.title)} step={step} />
                ))}
                {deploymentSteps.length === 0 && (
                  <div><span className="manage-status queued">empty</span><strong>暂无部署计划</strong><small>刷新 Services 后会从后端读取部署清单。</small></div>
                )}
              </div>
            </div>

            <div className="manage-service-grid manage-service-grid-advanced">
              {services.map((service) => (
                <ServiceCard key={`${service.name}-${service.url || service.backend || service.model || ''}`} service={service} />
              ))}
            </div>

            <div className="manage-card manage-test-card manage-card-deep">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Quick Probe</p>
                  <h3>一键测试</h3>
                </div>
                <span className="manage-panel-meta">直接从后端发起探测</span>
              </div>
              <div className="manage-form-row">
                <select className="manage-input" value={testTarget} onChange={(e) => setTestTarget(e.target.value)}>
                  <option value="llm">LLM</option>
                  <option value="embedding">Embedding</option>
                  <option value="rerank">Rerank</option>
                  <option value="openalex">OpenAlex</option>
                  <option value="crossref">Crossref</option>
                  <option value="semantic_scholar">Semantic Scholar</option>
                  <option value="pubmed">PubMed</option>
                  <option value="arxiv">arXiv</option>
                  <option value="biorxiv">bioRxiv</option>
                  <option value="medrxiv">medRxiv</option>
                </select>
                <input className="manage-input" value={testName} onChange={(e) => setTestName(e.target.value)} placeholder="Provider scope 或搜索词" />
              </div>
              <textarea className="manage-textarea" value={testConfig} onChange={(e) => setTestConfig(e.target.value)} placeholder='模型测试可填 {"MODEL":"...","API_URL":"...","API_KEY":"..."}；文献源测试可填 {"query":"Klebsiella pneumoniae antibiotic response","maxResults":10}' />
              <button className="manage-primary" onClick={handleTest} disabled={busy}>测试</button>
              {testResult && <pre className="manage-code">{JSON.stringify(testResult, null, 2)}</pre>}
            </div>
          </section>
        )}

        {tab === 'database' && (
          <section className="manage-section">
            <header className="manage-section-header manage-section-header-rich">
              <div>
                <p className="route-kicker">Data Atlas</p>
                <h2>数据库审计</h2>
                <p className="manage-tab-note">检查核心表行数、缓存分布和当前连接库，确认管理页看到的是实际运行数据库。</p>
              </div>
              <div className="manage-header-side">
                <span className="manage-muted">更新于 {formatIsoTime(databaseAudit?.updatedAt)}</span>
                <span className={`manage-live-badge ${databaseRefreshing ? 'refreshing' : ''}`}><i /> Live</span>
              </div>
            </header>
            {databaseError && <div className="manage-inline-error">数据库审计失败：{databaseError}</div>}
            {databaseRefreshing && <div className="manage-inline-muted">正在加载数据库审计...</div>}
            <div className="manage-card-grid manage-card-grid-rich">
              {databaseCards.map((card) => <MetricCard key={card.label} card={card} />)}
            </div>
            <FreePlanUsagePanel usage={freePlanUsage || databaseAudit?.freePlanUsage} />
            <div className="manage-card manage-card-deep">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Free-tier Capacity</p>
                  <h3>5000 篇召回容量估算</h3>
                </div>
                <span className="manage-status info">
                  TTL {formatNumber(databaseAudit?.capacity?.basis?.ttlHours || 24)}h
                </span>
              </div>
              <div className="manage-stage-list">
                <div>
                  <span className="manage-status ok">D1</span>
                  <strong>{formatRange(databaseAudit?.capacity?.perRunEstimate?.d1RowsWrittenRange)} rows/run</strong>
                  <small>只写状态、索引、usage、artifact 元数据；完整 paper JSON 不进 D1。任务内缓存可以保留，任务结束后清空。</small>
                </div>
                <div>
                  <span className="manage-status ok">D1 Artifact</span>
                  <strong>{formatRange(databaseAudit?.capacity?.perRunEstimate?.artifactBytesRange, formatBytes)} / run</strong>
                  <small>保存 search snapshot、model/compute 中间体、review.md、bibtex 和 metrics；默认 24h TTL，由业务 cleanup 删除临时 D1 artifact 分片。</small>
                </div>
                <div>
                  <span className="manage-status warn">Queue</span>
                  <strong>{formatNumber(databaseAudit?.capacity?.perRunEstimate?.queueOpsApprox || 0)} ops/run</strong>
                  <small>按 source/query、stage、batch 发消息，不按每篇论文发消息。</small>
                </div>
                <div>
                  <span className="manage-status info">Budget</span>
                  <strong>{formatNumber(databaseAudit?.capacity?.dailyRunBudget?.safeDailyRuns || 0)} runs/day</strong>
                  <small>当前保守瓶颈：{String(databaseAudit?.capacity?.dailyRunBudget?.limitingFactor || 'unknown')}；并发不是固定值，而是 D1 / Queue / provider-source 预算下的动态建议值。</small>
                </div>
              </div>
              <pre className="manage-code manage-config-view">{JSON.stringify(databaseAudit?.capacity || {}, null, 2)}</pre>
            </div>
            <div className="manage-card manage-card-deep">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Benchmark Evidence</p>
                  <h3>真实 run 性能证据</h3>
                </div>
                <span className="manage-status ok">review_20260522_023739_2baa18</span>
              </div>
              {(() => {
                const benchmark = databaseAudit?.capacity?.benchmarkEvidence?.review_20260522_023739_2baa18;
                const hotspots = Array.isArray(benchmark?.rust_python_hotspots) ? benchmark.rust_python_hotspots : [];
                return (
                  <>
                    <div className="manage-stage-list">
                      <div>
                        <span className="manage-status ok">Rust</span>
                        <strong>{formatNumber(benchmark?.rust_summary?.clusters || 0)} clusters · {formatMs(benchmark?.rust_summary?.cluster_papers_total_ms || 0)}</strong>
                        <small>真实 531 篇、1024 维 embedding；pair F1 {formatDecimal(benchmark?.cluster_golden?.pair_f1 || 0, 3)}，weighted Jaccard {formatDecimal(benchmark?.cluster_golden?.weighted_cluster_jaccard || 0, 3)}。</small>
                      </div>
                      <div>
                        <span className="manage-status warn">5k MVP</span>
                        <strong>{formatNumber(benchmark?.capacity_estimates?.free_mvp_5k?.safe_daily_runs_free || 0)} runs/day</strong>
                        <small>瓶颈 {String(benchmark?.capacity_estimates?.free_mvp_5k?.limiting_factor || 'unknown')} · D1 writes {formatRange([benchmark?.capacity_estimates?.free_mvp_5k?.d1_rows_written_low, benchmark?.capacity_estimates?.free_mvp_5k?.d1_rows_written_high])}。</small>
                      </div>
                      <div>
                        <span className="manage-status danger">80k</span>
                        <strong>{formatNumber(benchmark?.capacity_estimates?.recall_80k_native?.safe_daily_runs_free || 0)} free runs/day</strong>
                        <small>80k 召回不能按当前索引写入模型直接跑 Cloudflare Free，必须 D1 artifact 分片 + Cloudflare Rust/WASM Compute Worker 并继续降低 D1 writes、分片行数和 Worker CPU；免费 PostgreSQL / Redis 不进入主链路。</small>
                      </div>
                    </div>
                    <div className="manage-stage-list">
                      {hotspots.slice(0, 5).map((hotspot: any) => (
                        <div key={String(hotspot.group)}>
                          <span className="manage-status info">{String(hotspot.group)}</span>
                          <strong>{formatDecimal(hotspot.approx_speedup || 0, 1)}x speedup</strong>
                          <small>Python {formatMs(hotspot.python_wall_ms)} · Rust {formatMs(hotspot.rust_wall_ms)} · {String(hotspot.decision || '')}</small>
                        </div>
                      ))}
                    </div>
                  </>
                );
              })()}
            </div>
            <div className="manage-card manage-card-deep">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Connection</p>
                  <h3>当前连接库</h3>
                </div>
                <span className={`manage-status ${statusClass(databaseAudit?.database?.status)}`}>
                  {String(databaseAudit?.database?.status || 'unknown')}
                </span>
              </div>
              <pre className="manage-code manage-config-view">{JSON.stringify({
                url: databaseAudit?.database?.url || '',
                resolvedUrl: databaseAudit?.database?.resolvedUrl || '',
                storageKind: databaseAudit?.database?.storage?.kind || '',
                storageSource: databaseAudit?.database?.storage?.source || '',
                estimated: Boolean(databaseAudit?.database?.storage?.estimated),
                databasePath: databaseAudit?.database?.storage?.path || '',
                used: formatBytes(databaseAudit?.database?.storage?.usedBytes || 0),
                free: formatBytes(databaseAudit?.database?.storage?.freeBytes || 0),
                total: formatBytes(databaseAudit?.database?.storage?.totalBytes || 0),
                diskUsedPercent: databaseAudit?.database?.storage?.diskUsedPercent ?? '',
                error: databaseAudit?.database?.error || '',
              }, null, 2)}</pre>
            </div>
            <div className="manage-two-col">
              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Counts</p>
                    <h3>核心表统计</h3>
                  </div>
                </div>
                <pre className="manage-code manage-config-view">{JSON.stringify(databaseAudit?.database?.counts || {}, null, 2)}</pre>
              </div>
              <div className="manage-card manage-card-deep">
                <div className="manage-panel-head">
                  <div>
                    <p className="manage-panel-kicker">Cache</p>
                    <h3>缓存摘要</h3>
                  </div>
                </div>
                <div className="manage-recommendation-list">
                  {(databaseAudit?.notes || []).map((item) => (
                    <div className="manage-recommendation-item" key={item}>{item}</div>
                  ))}
                </div>
                <pre className="manage-code manage-config-view">{JSON.stringify(databaseAudit?.cache || {}, null, 2)}</pre>
              </div>
            </div>
            <div className="manage-card manage-card-deep">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Services</p>
                  <h3>关联服务卡片</h3>
                </div>
                <button className="manage-secondary" onClick={() => void loadDatabaseAudit(manageKey)} disabled={databaseRefreshing}>重新加载</button>
              </div>
              <div className="manage-service-grid manage-service-grid-advanced">
                {(databaseAudit?.services || []).slice(0, 8).map((service) => (
                  <ServiceCard key={`${service.name}-${service.url || service.backend || service.model || ''}`} service={service} />
                ))}
              </div>
            </div>
            <div className="manage-card manage-card-deep">
              <div className="manage-panel-head">
                <div>
                  <p className="manage-panel-kicker">Audit</p>
                  <h3>最近审计日志</h3>
                </div>
              </div>
              <div className="manage-stage-list">
                {(databaseAudit?.auditLogs || []).slice(0, 8).map((log) => {
                  let payload: Record<string, any> = {};
                  try {
                    payload = JSON.parse(String(log.payload_key || '{}'));
                  } catch {
                    payload = {};
                  }
                  return (
                    <div key={String(log.id)}>
                      <span className="manage-status info">{String(log.action || 'audit')}</span>
                      <strong>{String(log.actor_user_id || 'system')}</strong>
                      <small>{formatIsoTime(log.created_at)} · {String(log.target_type || 'target')}:{String(log.target_id || '')} · {JSON.stringify(payload)}</small>
                    </div>
                  );
                })}
                {(databaseAudit?.auditLogs || []).length === 0 && (
                  <div><span className="manage-status queued">empty</span><strong>暂无审计日志</strong><small>执行非 dry-run 清理、配置变更或账务操作后会写入这里。</small></div>
                )}
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
